import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import {
  assignStoreBuyerDealer,
  getStoreDealersDashboard,
  getStoreAdminUsers,
  getStoreBuyer,
  getStoreBuyersDashboard,
  importStoreContactBuyers,
  getStoreBuyersOrders,
  getStoreNotifications,
  getStoreBuyerActivities,
  getStoreShipmentInvoice,
  patchStoreShipmentStatus,
  updateStoreProjectStatus,
  getStoreProjectPayments,
  createStoreProjectPayment,
  updateStoreBuyer,
} from "../sales/service";
import {
  formatSessionDurationReadable,
  formatSessionStart,
} from "../../common/DateHelpers";
import "./StoreSalesDashboard.css";
import StoreSalesDashboardBuyersSection, {
  BuyerEditModalMobileField,
} from "./StoreSalesDashboardBuyersSection";
import StoreSalesDashboardDealersSection from "./StoreSalesDashboardDealersSection";
import StoreSalesDashboardNotificationsSection from "./StoreSalesDashboardNotificationsSection";
import StoreSalesDashboardCouponsSection from "./StoreSalesDashboardCouponsSection";
import StoreSalesDashboardOrdersSection from "./StoreSalesDashboardOrdersSection";
import StoreShipmentInvoiceModal from "../components/StoreShipmentInvoiceModal";
import {
  activityCountPhrase,
  buildSentTemplatesByUserId,
  BUYER_ENGAGEMENT_STATUS_OPTIONS,
  ensureArray,
  fmtHoverDateTime,
  canAddPaymentsInSalesModal,
  combinePaymentDateAndTime,
  defaultPaymentDateInputValue,
  defaultPaymentTimeOption,
  formatPaymentDateTime,
  PAYMENT_TIME_OPTIONS,
  fmtOrderRupee,
  fmtRelativeTime,
  formatStoreOrderLabel,
  formatContactImportStats,
  aggregateBuyerOrderMonthStatsForBuyerIds,
  applyBuyerTabFilters,
  buildBuyerAcquiredMonthFilterOptions,
  buildBuyerOrderMonthStats,
  buildCustomerOrderMonthKeys,
  BUYER_CITY_FILTER_TABS,
  BUYER_STATE_FILTER_TABS,
  firstStoreUserForBuyer,
  mapBuyersToRows,
  mapDealersToRows,
  buildBuyerCityOptionsFromRows,
  orderBuyerStateOptions,
  patchBuyerDashboardLists,
  metaToKeyValueLines,
  buildStoreSalesDashboardSearchParams,
  normalizeStoreSalesDashboardSearchParams,
  parseStoreSalesDashboardUrlParams,
  NOTIFICATION_SORT_DEFAULT,
  ORDERS_SORT_DEFAULT,
  sortNotificationRows,
  matchesOrdersSampleFilter,
  matchesOrdersMonthFilter,
  matchesOrdersStatusFilter,
  sortOrdersRows,
  sortRows,
  STORE_DASHBOARD_FILTER_INPUT_CLASS,
  STORE_BUYER_FIT_VALUES,
  STORE_BUYER_CONVICTION_VALUES,
  committedDatePickerOptions,
  buildCommittedDateFilterOptions,
  COMMITTED_DATE_FILTER_NONE,
  toErrorText,
  tsOrZero,
  uniqById,
} from "./storeSalesDashboardHelpers";

const sessionDurationMs = (s) => {
  const start = tsOrZero(s?.started_at);
  const end = tsOrZero(s?.last_activity_at);
  return Math.max(0, end - start);
};

/** sorts session rows; default latest-first uses sessionLastAt + desc */
const sortSessionsRows = (sessions, sortConfig) => {
  const list = [...ensureArray(sessions)];
  const { key, direction } = sortConfig;
  const factor = direction === "asc" ? 1 : -1;
  list.sort((a, b) => {
    if (key === "sessionStart") {
      return (tsOrZero(a?.started_at) - tsOrZero(b?.started_at)) * factor;
    }
    if (key === "sessionLastAt") {
      return (
        (tsOrZero(a?.last_activity_at) - tsOrZero(b?.last_activity_at)) * factor
      );
    }
    if (key === "sessionDuration") {
      return (sessionDurationMs(a) - sessionDurationMs(b)) * factor;
    }
    if (key === "sessionActivityCount") {
      return (
        (Number(a?.activities_count ?? 0) - Number(b?.activities_count ?? 0)) *
        factor
      );
    }
    return 0;
  });
  return list;
};

const SESSIONS_SORT_DEFAULT = {
  key: "sessionLastAt",
  direction: "desc",
};

const BUYER_TABS = ["customers", "prospects", "leads", "contacts"];
const BUYER_SEARCH_TABS = ["customers", "prospects", "leads"];

/** Set true to show the Dealers tab in the dashboard nav. */
const SHOW_DEALERS_TAB = false;

const BUYER_PROFILE_OPTIONS = [
  { value: "", label: "—" },
  { value: "contractor", label: "Contractor" },
  { value: "home_owner", label: "Home owner" },
  { value: "interior_designer", label: "Interior designer" },
  { value: "kitchen_company", label: "Kitchen company" },
  { value: "architect", label: "Architect" },
  { value: "carpenter", label: "Carpenter" },
  { value: "vendor", label: "Vendor" },
  { value: "oems", label: "OEMs" },
  { value: "builder", label: "Builder" },
];

const BuyerActivitiesPaneContent = ({ pane, setPane }) => {
  const visibleActivities = useMemo(() => {
    if (!pane.activitySessionFilter || pane.activitySessionFilter === "all") {
      return pane.activitiesRows;
    }
    return pane.activitiesRows.filter(
      (a) =>
        String(a.store_activity_session_id) ===
        String(pane.activitySessionFilter)
    );
  }, [pane.activitiesRows, pane.activitySessionFilter]);

  const sessionsSortCfg = pane.sessionsSortConfig || SESSIONS_SORT_DEFAULT;

  const sortedSessions = useMemo(
    () => sortSessionsRows(pane.sessionsRows, sessionsSortCfg),
    [pane.sessionsRows, sessionsSortCfg]
  );

  const handleSessionsSort = (sortKey) => {
    setPane((prev) => {
      const prevCfg = prev.sessionsSortConfig || SESSIONS_SORT_DEFAULT;
      return {
        ...prev,
        sessionsSortConfig: {
          key: sortKey,
          direction:
            prevCfg.key === sortKey && prevCfg.direction === "asc"
              ? "desc"
              : "asc",
        },
      };
    });
  };

  const renderSessionsSortIcon = (columnKey) => {
    if (sessionsSortCfg.key !== columnKey) return "⇅";
    return sessionsSortCfg.direction === "asc" ? "▲" : "▼";
  };

  if (pane.error) {
    return (
      <Alert color="danger" fade={false}>
        {pane.error}
      </Alert>
    );
  }

  if (pane.loading) {
    return (
      <div className="py-4 text-center">
        <Spinner />
      </div>
    );
  }

  const setPaneTab = (tab) =>
    setPane((prev) => ({
      ...prev,
      paneTab: tab === "sessions" ? "sessions" : "activities",
    }));

  return (
    <>
      <Nav tabs className="mb-3">
        <NavItem>
          <NavLink
            href="#"
            className="fw-bold"
            active={pane.paneTab === "activities"}
            onClick={(e) => {
              e.preventDefault();
              setPaneTab("activities");
            }}
          >
            Activities
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            href="#"
            className="fw-bold"
            active={pane.paneTab === "sessions"}
            onClick={(e) => {
              e.preventDefault();
              setPaneTab("sessions");
            }}
          >
            Sessions
          </NavLink>
        </NavItem>
      </Nav>

      {pane.paneTab === "activities" ? (
        <>
          <div className="mb-3">
            <label className="form-label small text-muted mb-1 d-block">
              Session
            </label>
            <Input
              bsSize="sm"
              type="select"
              className={STORE_DASHBOARD_FILTER_INPUT_CLASS}
              value={pane.activitySessionFilter || "all"}
              onChange={(e) =>
                setPane((prev) => ({
                  ...prev,
                  activitySessionFilter: e.target.value,
                }))
              }
            >
              <option value="all">All sessions</option>
              {ensureArray(pane.sessionsRows).map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {formatSessionStart(s.started_at)}
                </option>
              ))}
            </Input>
          </div>
          <Table bordered hover responsive className="mb-0 store-sales-table">
            <thead className="store-sales-table__head">
              <tr>
                <th>Action</th>
                <th>Details</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {visibleActivities.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-muted text-center py-4">
                    No activities found.
                  </td>
                </tr>
              ) : (
                visibleActivities.map((a) => (
                  <tr key={a.id}>
                    <td>{a.action}</td>
                    <td style={{ whiteSpace: "pre-wrap" }}>
                      {metaToKeyValueLines(a?.meta).length === 0 ? (
                        "-"
                      ) : (
                        <div className="d-flex flex-column gap-1">
                          {metaToKeyValueLines(a?.meta).map((row) => (
                            <div key={row.key}>
                              <span className="fw-semibold">{row.key}</span>
                              {": "}
                              <span>{row.value}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td title={fmtHoverDateTime(a.created_at)}>
                      {fmtRelativeTime(a.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </>
      ) : (
        <Table bordered hover responsive className="mb-0 store-sales-table">
          <thead className="store-sales-table__head">
            <tr>
              <th
                role="button"
                onClick={() => handleSessionsSort("sessionStart")}
              >
                Start {renderSessionsSortIcon("sessionStart")}
              </th>
              <th
                role="button"
                onClick={() => handleSessionsSort("sessionDuration")}
              >
                Duration {renderSessionsSortIcon("sessionDuration")}
              </th>
              <th
                role="button"
                onClick={() => handleSessionsSort("sessionActivityCount")}
              >
                Activities {renderSessionsSortIcon("sessionActivityCount")}
              </th>
            </tr>
          </thead>
          <tbody>
            {ensureArray(pane.sessionsRows).length === 0 ? (
              <tr>
                <td colSpan={3} className="text-muted text-center py-4">
                  No sessions found.
                </td>
              </tr>
            ) : (
              sortedSessions.map((s) => (
                <tr key={s.id}>
                  <td title={fmtHoverDateTime(s.started_at)}>
                    {formatSessionStart(s.started_at)}
                  </td>
                  <td>
                    {formatSessionDurationReadable(
                      s.started_at,
                      s.last_activity_at
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 align-baseline"
                      onClick={() =>
                        setPane((prev) => ({
                          ...prev,
                          paneTab: "activities",
                          activitySessionFilter: String(s.id),
                        }))
                      }
                    >
                      {activityCountPhrase(s.activities_count)}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}
    </>
  );
};

const fmtCurrency = (value) => {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "-";
  return `Rs ${num.toLocaleString("en-IN")}`;
};

const fmtProjectScore = (value) => {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return 0;
  return num;
};

const BuyerInfoContent = ({ buyer, onProjectClick, detailLoading }) => {
  const users = ensureArray(buyer?.users);
  const projects = ensureArray(buyer?.projects);
  const [usersOpen, setUsersOpen] = useState(false);
  const [userSortConfig, setUserSortConfig] = useState({
    key: "last_activity_at",
    direction: "desc",
  });
  const [projectSortConfig, setProjectSortConfig] = useState({
    key: "last_activity_at",
    direction: "desc",
  });

  const handleUserSort = (key) => {
    setUserSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };
  const handleProjectSort = (key) => {
    setProjectSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };
  const renderInnerSortIcon = (cfg, key) => {
    if (cfg.key !== key) return "⇅";
    return cfg.direction === "asc" ? "▲" : "▼";
  };

  const sortedUsers = useMemo(() => {
    const next = [...users];
    const factor = userSortConfig.direction === "asc" ? 1 : -1;
    next.sort((a, b) => {
      if (userSortConfig.key === "name" || userSortConfig.key === "mobile") {
        return (
          String(a?.[userSortConfig.key] || "").localeCompare(
            String(b?.[userSortConfig.key] || "")
          ) * factor
        );
      }
      if (userSortConfig.key === "last_activity_at") {
        return (
          (tsOrZero(a?.last_activity_at) - tsOrZero(b?.last_activity_at)) *
          factor
        );
      }
      return 0;
    });
    return next;
  }, [users, userSortConfig]);

  const sortedProjects = useMemo(() => {
    const next = [...projects];
    const factor = projectSortConfig.direction === "asc" ? 1 : -1;
    next.sort((a, b) => {
      if (
        projectSortConfig.key === "name" ||
        projectSortConfig.key === "status"
      ) {
        return (
          String(a?.[projectSortConfig.key] || "").localeCompare(
            String(b?.[projectSortConfig.key] || "")
          ) * factor
        );
      }
      if (
        projectSortConfig.key === "value" ||
        projectSortConfig.key === "score"
      ) {
        return (
          (Number(a?.[projectSortConfig.key] || 0) -
            Number(b?.[projectSortConfig.key] || 0)) *
          factor
        );
      }
      if (projectSortConfig.key === "last_activity_at") {
        return (
          (tsOrZero(a?.last_activity_at) - tsOrZero(b?.last_activity_at)) *
          factor
        );
      }
      return 0;
    });
    return next;
  }, [projects, projectSortConfig]);

  return (
    <>
      {detailLoading ? (
        <div className="text-center py-4">
          <Spinner size="sm" className="me-2" />
          Loading customer details…
        </div>
      ) : !buyer ? (
        <Alert color="secondary" className="mb-0" fade={false}>
          No buyer info available.
        </Alert>
      ) : (
        <>
          <div className="mb-3 position-relative">
            <div className="d-flex align-items-start justify-content-between gap-2">
              <h6 className="fw-bold mb-1">
                {buyer.name} <span className="text-muted">#{buyer.id}</span>
              </h6>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                aria-label="Show users"
                onClick={() => setUsersOpen((prev) => !prev)}
              >
                <i className="fa-solid fa-user" aria-hidden />
              </button>
            </div>
            {usersOpen ? (
              <div className="store-sales-users-popover shadow">
                <div className="store-sales-users-popover__header">
                  <span>Users</span>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    aria-label="Close"
                    onClick={() => setUsersOpen(false)}
                  />
                </div>
                <Table
                  bordered
                  responsive
                  size="sm"
                  className="mb-0 store-sales-table"
                >
                  <thead className="store-sales-table__head">
                    <tr>
                      <th role="button" onClick={() => handleUserSort("name")}>
                        Name {renderInnerSortIcon(userSortConfig, "name")}
                      </th>
                      <th
                        role="button"
                        onClick={() => handleUserSort("mobile")}
                      >
                        Mobile {renderInnerSortIcon(userSortConfig, "mobile")}
                      </th>
                      <th
                        role="button"
                        onClick={() => handleUserSort("last_activity_at")}
                      >
                        Last Activity{" "}
                        {renderInnerSortIcon(
                          userSortConfig,
                          "last_activity_at"
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="text-muted text-center">
                          No users found.
                        </td>
                      </tr>
                    ) : (
                      sortedUsers.map((user) => (
                        <tr key={user.id}>
                          <td>{user.name}</td>
                          <td>{user.mobile || "-"}</td>
                          <td title={fmtHoverDateTime(user.last_activity_at)}>
                            {fmtRelativeTime(user.last_activity_at)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            ) : null}
            <div className="small text-muted">
              Lead Source:{" "}
              <span className="text-dark fw-semibold">
                {buyer.lead_source || "-"}
              </span>
            </div>
            {buyer?.lead_source_url ? (
              <div className="small text-muted text-break">
                URL: {buyer.lead_source_url}
              </div>
            ) : null}
            {buyer?.lead_source_meta &&
            Object.keys(buyer.lead_source_meta).length > 0 ? (
              <div className="small text-muted">
                Meta:{" "}
                {Object.entries(buyer.lead_source_meta)
                  .map(([k, v]) => `${k}=${v}`)
                  .join(", ")}
              </div>
            ) : null}
          </div>

          <div className="mb-3">
            <h6 className="fw-semibold mb-2">Projects</h6>
            <Table
              bordered
              responsive
              size="sm"
              className="mb-0 store-sales-table"
            >
              <thead className="store-sales-table__head">
                <tr>
                  <th role="button" onClick={() => handleProjectSort("name")}>
                    Name {renderInnerSortIcon(projectSortConfig, "name")}
                  </th>
                  <th role="button" onClick={() => handleProjectSort("value")}>
                    Value {renderInnerSortIcon(projectSortConfig, "value")}
                  </th>
                  <th role="button" onClick={() => handleProjectSort("status")}>
                    Status {renderInnerSortIcon(projectSortConfig, "status")}
                  </th>
                  <th
                    role="button"
                    onClick={() => handleProjectSort("last_activity_at")}
                  >
                    Last Activity{" "}
                    {renderInnerSortIcon(projectSortConfig, "last_activity_at")}
                  </th>
                  <th role="button" onClick={() => handleProjectSort("score")}>
                    Score {renderInnerSortIcon(projectSortConfig, "score")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {projects.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-muted text-center">
                      No projects found.
                    </td>
                  </tr>
                ) : (
                  sortedProjects.map((project) => (
                    <tr key={project.id}>
                      <td>
                        <button
                          type="button"
                          className="btn btn-link p-0 text-decoration-none"
                          onClick={() => onProjectClick(project)}
                        >
                          {formatStoreOrderLabel(project)}
                        </button>
                      </td>
                      <td>{fmtCurrency(project.value)}</td>
                      <td className="text-capitalize">
                        {project.status || "-"}
                      </td>
                      <td title={fmtHoverDateTime(project.last_activity_at)}>
                        {fmtRelativeTime(project.last_activity_at)}
                      </td>
                      <td className="fw-semibold">
                        {fmtProjectScore(project.score)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </>
  );
};

const StoreSalesDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialUrlStateRef = useRef(null);
  if (initialUrlStateRef.current === null) {
    initialUrlStateRef.current = parseStoreSalesDashboardUrlParams(searchParams);
  }
  const initialUrlState = initialUrlStateRef.current;

  const lastSyncedUrlRef = useRef(
    normalizeStoreSalesDashboardSearchParams(searchParams)
  );
  const searchParamsForMergeRef = useRef(searchParams);
  searchParamsForMergeRef.current = searchParams;

  const [activeTab, setActiveTab] = useState(initialUrlState.activeTab);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState(initialUrlState.searchText);
  const [sortConfig, setSortConfig] = useState(initialUrlState.sortConfig);
  const [buyers, setBuyers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [buyerDetailLoading, setBuyerDetailLoading] = useState(false);
  const [isMobileDetailsOpen, setIsMobileDetailsOpen] = useState(false);
  const [activitiesPane, setActivitiesPane] = useState({
    isOpen: false,
    buyerId: null,
    buyerName: "",
    buyerMobile: "",
    loading: false,
    error: null,
    activitiesRows: [],
    sessionsRows: [],
    paneTab: "activities",
    activitySessionFilter: "all",
    sessionsSortConfig: SESSIONS_SORT_DEFAULT,
  });
  const [actionError, setActionError] = useState(null);
  const [importSuccess, setImportSuccess] = useState(null);
  const [importCsvLoading, setImportCsvLoading] = useState(false);
  const contactCsvInputRef = useRef(null);
  const [dealers, setDealers] = useState([]);
  const [dealersLoading, setDealersLoading] = useState(true);
  const [dealersError, setDealersError] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState(null);
  const [ordersStatusFilter, setOrdersStatusFilter] = useState([]);
  const [ordersSampleFilter, setOrdersSampleFilter] = useState("");
  const [ordersMonthFilter, setOrdersMonthFilter] = useState([]);
  const [ordersSortConfig, setOrdersSortConfig] = useState(ORDERS_SORT_DEFAULT);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState(null);
  const [notificationNameFilter, setNotificationNameFilter] = useState("");
  const [notificationTemplateFilter, setNotificationTemplateFilter] =
    useState("");
  const [notificationStatusFilter, setNotificationStatusFilter] = useState("");
  const [notificationSortConfig, setNotificationSortConfig] = useState(
    NOTIFICATION_SORT_DEFAULT
  );


  const [highlightBuyerId, setHighlightBuyerId] = useState(null);
  const [assignDealerModal, setAssignDealerModal] = useState({
    isOpen: false,
    buyerId: null,
    buyerName: "",
    selectedDealerId: "",
    loading: false,
  });
  const [dispatchShipmentModal, setDispatchShipmentModal] = useState({
    isOpen: false,
    shipmentId: null,
    shipmentLabel: "",
    loading: false,
  });
  const [invoiceModal, setInvoiceModal] = useState({
    isOpen: false,
    loading: false,
    error: null,
    shipmentLabel: "",
    invoice: null,
  });

  const [deliverShipmentModal, setDeliverShipmentModal] = useState({
    isOpen: false,
    shipmentId: null,
    shipmentLabel: "",
    loading: false,
  });
  const [paymentsModal, setPaymentsModal] = useState({
    isOpen: false,
    projectId: null,
    projectName: "",
    mode: "",
    status: "",
    loading: false,
    saving: false,
    error: null,
    payments: [],
    total: 0,
    value: "",
    date: "",
    time: "",
  });
  const [isMobileView, setIsMobileView] = useState(
    () => window.innerWidth < 768
  );
  const [buyerVisibilityFilter, setBuyerVisibilityFilter] = useState(
    initialUrlState.buyerVisibilityFilter
  );
  const [buyerCityFiltersByTab, setBuyerCityFiltersByTab] = useState(
    initialUrlState.buyerCityFiltersByTab
  );
  const [buyerStateFiltersByTab, setBuyerStateFiltersByTab] = useState(
    initialUrlState.buyerStateFiltersByTab
  );
  const [buyerPpmFilter, setBuyerPpmFilter] = useState(
    initialUrlState.buyerPpmFilter
  );
  const [buyerEngagementFilter, setBuyerEngagementFilter] = useState(
    initialUrlState.buyerEngagementFilter
  );
  const [buyerSalesOwnerFilter, setBuyerSalesOwnerFilter] = useState(
    initialUrlState.buyerSalesOwnerFilter
  );
  const [storeAdminUsers, setStoreAdminUsers] = useState([]);
  const [eventSentFilter, setEventSentFilter] = useState([]);
  const [eventNotSentFilter, setEventNotSentFilter] = useState([]);
  const [buyerProfileTypeFilter, setBuyerProfileTypeFilter] = useState(
    initialUrlState.buyerProfileTypeFilter
  );
  const [buyerContactStatusFilter, setBuyerContactStatusFilter] = useState(
    initialUrlState.buyerContactStatusFilter
  );
  const [buyerSourceFilter, setBuyerSourceFilter] = useState(
    initialUrlState.buyerSourceFilter
  );
  const [profileFitFilter, setProfileFitFilter] = useState(
    initialUrlState.profileFitFilter
  );
  const [convictionFilter, setConvictionFilter] = useState(
    initialUrlState.convictionFilter
  );
  const [committedDateFilter, setCommittedDateFilter] = useState(
    initialUrlState.committedDateFilter
  );
  const [buyerAcquiredMonthFilter, setBuyerAcquiredMonthFilter] = useState(
    initialUrlState.buyerAcquiredMonthFilter
  );
  const [selectedProspectBuyerIds, setSelectedProspectBuyerIds] = useState([]);
  const [selectedCustomerBuyerIds, setSelectedCustomerBuyerIds] = useState([]);
  const [buyersTableViewMode, setBuyersTableViewMode] = useState(
    initialUrlState.buyersTableViewMode
  );

  const [editBuyerModal, setEditBuyerModal] = useState({
    isOpen: false,
    mode: "single",
    buyerId: null,
    bulkBuyerIds: [],
    buyerUserName: "",
    buyerUserMobile: "",
    loading: false,
    saving: false,
    error: null,
    salesNotes: [],
    form: {
      user_name: "",
      name: "",
      city: "",
      profile_type: "",
      status: "prospect",
      sub_status: "",
      engagement_status: "new",
      sales_owner_id: "",
      new_remark: "",
      projects_per_month: "",
      followup_date: "",
      fit: "unknown",
      conviction: "no",
      committed_date: "",
    },
    touched: {},
  });

  const BUYER_STATUS_OPTIONS = ["lead", "prospect", "customer", "archived"];
  const BUYER_SUB_STATUS_OPTIONS = [
    "",
    "contact_none",
    "contact_marketed",
    "contact_enquired",
    "contact_rejected",
    "contact_not_interested",
    "prospect_rejected",
    "prospect_not_serviceable",
    "prospect_not_interested",
    "archived_junk",
    "archived_testing",
    "archived_duplicate",
  ];
  const BUYER_PROSPECT_SUB_STATUS_OPTIONS = [
    "",
    "prospect_rejected",
    "prospect_not_serviceable",
    "prospect_not_interested",
  ];
  const buyerCityFilter = BUYER_CITY_FILTER_TABS.includes(activeTab)
    ? buyerCityFiltersByTab[activeTab] || []
    : [];

  const buyerStateFilter = BUYER_STATE_FILTER_TABS.includes(activeTab)
    ? buyerStateFiltersByTab[activeTab] || []
    : [];

  const setBuyerCityFilter = useCallback(
    (next) => {
      if (!BUYER_CITY_FILTER_TABS.includes(activeTab)) return;
      setBuyerCityFiltersByTab((prev) => ({
        ...prev,
        [activeTab]: next,
      }));
    },
    [activeTab]
  );

  const setBuyerStateFilter = useCallback(
    (next) => {
      if (!BUYER_STATE_FILTER_TABS.includes(activeTab)) return;
      setBuyerStateFiltersByTab((prev) => ({
        ...prev,
        [activeTab]: next,
      }));
    },
    [activeTab]
  );

  const applyDashboardUrlState = useCallback((parsed) => {
    setActiveTab(parsed.activeTab);
    setSearchText(parsed.searchText);
    setSortConfig(parsed.sortConfig);
    setBuyerVisibilityFilter(parsed.buyerVisibilityFilter);
    setBuyerCityFiltersByTab(parsed.buyerCityFiltersByTab);
    setBuyerStateFiltersByTab(parsed.buyerStateFiltersByTab);
    setBuyerPpmFilter(parsed.buyerPpmFilter);
    setBuyerEngagementFilter(parsed.buyerEngagementFilter);
    setBuyerSalesOwnerFilter(parsed.buyerSalesOwnerFilter);
    setBuyerProfileTypeFilter(parsed.buyerProfileTypeFilter);
    setBuyerContactStatusFilter(parsed.buyerContactStatusFilter);
    setBuyerSourceFilter(parsed.buyerSourceFilter);
    setProfileFitFilter(parsed.profileFitFilter);
    setConvictionFilter(parsed.convictionFilter);
    setCommittedDateFilter(parsed.committedDateFilter);
    setBuyerAcquiredMonthFilter(parsed.buyerAcquiredMonthFilter);
    setBuyersTableViewMode(parsed.buyersTableViewMode);
  }, []);

  useEffect(() => {
    const normalized = normalizeStoreSalesDashboardSearchParams(searchParams);
    if (normalized === lastSyncedUrlRef.current) return;
    lastSyncedUrlRef.current = normalized;
    applyDashboardUrlState(parseStoreSalesDashboardUrlParams(searchParams));
  }, [searchParams, applyDashboardUrlState]);

  useEffect(() => {
    const built = buildStoreSalesDashboardSearchParams(
      {
        activeTab,
        searchText,
        sortConfig,
        buyerVisibilityFilter,
        buyerCityFiltersByTab,
        buyerStateFiltersByTab,
        buyerPpmFilter,
        buyerEngagementFilter,
        buyerSalesOwnerFilter,
        buyerProfileTypeFilter,
        buyerContactStatusFilter,
        buyerSourceFilter,
        profileFitFilter,
        convictionFilter,
        committedDateFilter,
        buyerAcquiredMonthFilter,
        buyersTableViewMode,
      },
      searchParamsForMergeRef.current
    );
    const normalized = normalizeStoreSalesDashboardSearchParams(built);
    if (normalized === lastSyncedUrlRef.current) return;
    lastSyncedUrlRef.current = normalized;
    setSearchParams(built, { replace: true });
  }, [
    activeTab,
    searchText,
    sortConfig,
    buyerVisibilityFilter,
    buyerCityFiltersByTab,
    buyerStateFiltersByTab,
    buyerPpmFilter,
    buyerEngagementFilter,
    buyerSalesOwnerFilter,
    buyerProfileTypeFilter,
    buyerContactStatusFilter,
    buyerSourceFilter,
    profileFitFilter,
    convictionFilter,
    committedDateFilter,
    buyerAcquiredMonthFilter,
    buyersTableViewMode,
    setSearchParams,
  ]);

  const selectBuyerTab = useCallback((tab) => {
    setActiveTab(tab);
    setBuyerVisibilityFilter("all");
    setSelectedProspectBuyerIds([]);
    setSelectedCustomerBuyerIds([]);
    if (tab === "customers") {
      setBuyerPpmFilter([]);
      setBuyerEngagementFilter([]);
      setBuyerSalesOwnerFilter([]);
      setBuyerProfileTypeFilter([]);
      setBuyersTableViewMode("compact");
    }
    if (tab !== "contacts") {
      setBuyerContactStatusFilter("");
      setBuyerSourceFilter("");
    }
    if (tab !== "prospects") {
      setProfileFitFilter([]);
      setConvictionFilter([]);
      setCommittedDateFilter([]);
    }
    if (tab !== "customers") {
      setBuyerAcquiredMonthFilter([]);
    }
  }, []);

  const validateBuyerEditForm = (form, mode = "single") => {
    const errors = {};
    const status = String(form.status || "").trim();
    const sub = String(form.sub_status || "").trim();
    const eng = String(form.engagement_status || "").trim();
    const ppmRaw = form.projects_per_month;

    if (mode === "bulk") {
      const fit = String(form.fit || "").trim();
      const eng = String(form.engagement_status || "").trim();
      const sub = String(form.sub_status || "").trim();
      const salesOwner = String(form.sales_owner_id || "").trim();
      const conviction = String(form.conviction || "").trim();
      const committedDate = String(form.committed_date || "").trim();

      if (!fit && !eng && !sub && !salesOwner && !conviction && !committedDate) {
        errors.bulk_fields = "Select at least one field to update.";
      }
      if (fit && !STORE_BUYER_FIT_VALUES.includes(fit)) {
        errors.fit = "Invalid fit value.";
      }
      if (
        conviction &&
        !STORE_BUYER_CONVICTION_VALUES.includes(conviction)
      ) {
        errors.conviction = "Invalid conviction value.";
      }
      if (eng && !BUYER_ENGAGEMENT_STATUS_OPTIONS.includes(eng)) {
        errors.engagement_status = "Invalid engagement status.";
      }
      if (sub && !BUYER_PROSPECT_SUB_STATUS_OPTIONS.includes(sub)) {
        errors.sub_status = "Invalid prospect status.";
      }
      if (committedDate) {
        const allowed = committedDatePickerOptions(
          new Date(),
          committedDate
        ).some((opt) => opt.value === committedDate);
        if (!allowed) {
          errors.committed_date = "Invalid committed date.";
        }
      }

      return errors;
    }

    if (!BUYER_STATUS_OPTIONS.includes(status)) {
      errors.status = "Status is required.";
    }

    if (sub && !BUYER_SUB_STATUS_OPTIONS.includes(sub)) {
      errors.sub_status = "Invalid sub status.";
    }

    if (sub.startsWith("contact_") && status !== "contact") {
      errors.sub_status = "Contact sub status requires status = contact.";
    }
    if (status === "contact" && sub && !sub.startsWith("contact_")) {
      errors.sub_status = "Invalid contact sub status.";
    }
    if (sub.startsWith("prospect_") && status !== "prospect") {
      errors.sub_status = "Prospect sub status requires status = prospect.";
    }
    if (sub.startsWith("archived_") && status !== "archived") {
      errors.sub_status = "Archived sub status requires status = archived.";
    }
    if (status === "contact" && !sub) {
      errors.sub_status = "Contact status requires a sub status.";
    }

    if (!eng || !BUYER_ENGAGEMENT_STATUS_OPTIONS.includes(eng)) {
      errors.engagement_status = "Engagement status is required.";
    }

    if (ppmRaw !== null && typeof ppmRaw !== "undefined" && ppmRaw !== "") {
      const n = Number(ppmRaw);
      if (!Number.isFinite(n) || !Number.isInteger(n) || n <= 0) {
        errors.projects_per_month = "Must be a whole number > 0.";
      }
    }

    const committedDate = String(form.committed_date || "").trim();
    if (committedDate) {
      const allowed = committedDatePickerOptions(
        new Date(),
        committedDate
      ).some((opt) => opt.value === committedDate);
      if (!allowed) {
        errors.committed_date = "Invalid committed date.";
      }
    }

    return errors;
  };

  useEffect(() => {
    const onResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await getStoreBuyersDashboard();
      setLeads(ensureArray(payload?.leads));
      setContacts(ensureArray(payload?.contacts));
      const buyerRows = uniqById([
        ...ensureArray(payload?.prospects),
        ...ensureArray(payload?.customers),
      ]);
      setBuyers(buyerRows);
    } catch (e) {
      setError(toErrorText(e, "Unable to load store buyers dashboard."));
    } finally {
      setLoading(false);
    }
  };

  const handleContactCsvButtonClick = () => {
    contactCsvInputRef.current?.click();
  };

  const handleContactCsvFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      setImportSuccess(null);
      setActionError("Please upload a valid CSV file.");
      return;
    }

    setActionError(null);
    setImportSuccess(null);
    setImportCsvLoading(true);
    try {
      const result = await importStoreContactBuyers(file);
      await loadDashboard();
      setImportSuccess(formatContactImportStats(result?.stats));
    } catch (err) {
      setImportSuccess(null);
      setActionError(toErrorText(err, "Import failed."));
    } finally {
      setImportCsvLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const payload = await getStoreAdminUsers();
        if (!cancelled) {
          setStoreAdminUsers(ensureArray(payload?.admin_users));
        }
      } catch {
        if (!cancelled) setStoreAdminUsers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadDashboard();
    loadDealers();
  }, []);

  const loadDealers = async () => {
    setDealersLoading(true);
    setDealersError(null);
    try {
      const payload = await getStoreDealersDashboard();
      setDealers(ensureArray(payload?.dealers));
    } catch (e) {
      setDealersError(toErrorText(e, "Unable to load dealers."));
    } finally {
      setDealersLoading(false);
    }
  };

  const loadNotifications = async () => {
    setNotificationsLoading(true);
    setNotificationsError(null);
    try {
      const payload = await getStoreNotifications();
      const rows = ensureArray(payload?.notifications).map((notification) => ({
        id: notification?.id ?? null,
        storeUserId: notification?.store_user_id ?? null,
        name: String(notification?.name || "").trim() || "-",
        mobile: String(notification?.mobile || "").trim() || "-",
        templateName: String(notification?.template_name || "").trim() || "-",
        deliveryStatus:
          String(notification?.delivery_status || "").trim() || "-",
        createdAt: notification?.created_at || null,
        statusUpdatedAt: notification?.status_updated_at || null,
        recipientType:
          String(notification?.recipient_type || "other").trim() || "other",
      }));
      setNotifications(rows);
    } catch (e) {
      setNotificationsError(toErrorText(e, "Unable to load notifications."));
    } finally {
      setNotificationsLoading(false);
    }
  };

  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    setOrdersError(null);
    try {
      const payload = await getStoreBuyersOrders();
      setOrders(ensureArray(payload?.orders));
    } catch (e) {
      setOrdersError(toErrorText(e, "Unable to load orders."));
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== "dealers") return;
    if (dealersLoading) return;
    if (dealers.length > 0) return;
    loadDealers();
  }, [activeTab]);

  useLayoutEffect(() => {
    if (activeTab === "orders" || activeTab === "customers") {
      setOrdersLoading(true);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "orders" && activeTab !== "customers") return;
    loadOrders();
  }, [activeTab, loadOrders]);

  useLayoutEffect(() => {
    if (activeTab === "notifications" && notifications.length === 0) {
      setNotificationsLoading(true);
    }
  }, [activeTab, notifications.length]);

  useEffect(() => {
    if (activeTab !== "notifications") return;
    if (notifications.length > 0) return;
    loadNotifications();
  }, [activeTab, notifications.length]);

  useEffect(() => {
    if (!BUYER_TABS.includes(activeTab)) return;
    if (notificationsLoading) return;
    if (notifications.length > 0) return;
    loadNotifications();
  }, [activeTab]);

  useEffect(() => {
    if (highlightBuyerId == null) return undefined;
    const t = window.setTimeout(() => setHighlightBuyerId(null), 2600);
    return () => window.clearTimeout(t);
  }, [highlightBuyerId]);

  useEffect(() => {
    if (highlightBuyerId == null) return undefined;
    if (!BUYER_TABS.includes(activeTab)) return undefined;
    if (loading) return undefined;

    const id = window.requestAnimationFrame(() => {
      const el = document.querySelector(
        `[data-store-dashboard-buyer-row="${highlightBuyerId}"]`
      );
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => window.cancelAnimationFrame(id);
  }, [highlightBuyerId, activeTab, loading, searchText, buyers.length]);

  const handleOpenBuyer = async (buyerRow) => {
    if (!BUYER_TABS.includes(activeTab)) return;
    if (!buyerRow) return;

    const buyerId = buyerRow?.id;
    setSelectedBuyer(buyerRow.raw || null);
    if (isMobileView) setIsMobileDetailsOpen(true);

    if (!buyerId) return;

    setBuyerDetailLoading(true);
    try {
      const payload = await getStoreBuyer(buyerId);
      if (payload?.buyer) setSelectedBuyer(payload.buyer);
    } catch (e) {
      setActionError(toErrorText(e, "Unable to load buyer details."));
    } finally {
      setBuyerDetailLoading(false);
    }
  };

  const openEditBuyerModal = async (buyerRow) => {
    const buyer = buyerRow?.raw || {};
    const buyerId = buyerRow?.id ?? null;
    const rowUserName = String(buyerRow?.firstUserName || "").trim();
    const baseForm = {
      user_name: rowUserName === "—" ? "" : rowUserName,
      name: String(buyer?.name || "").trim(),
      city: String(buyer?.city || "").trim(),
      profile_type: String(buyer?.profile_type || "").trim(),
      status: String(buyer?.status || "prospect").trim(),
      sub_status: String(buyer?.sub_status || "").trim(),
      engagement_status: String(buyer?.engagement_status || "new").trim(),
      sales_owner_id:
        buyer?.sales_owner_id == null ? "" : String(buyer.sales_owner_id),
      new_remark: "",
      projects_per_month:
        buyer?.projects_per_month == null
          ? ""
          : String(buyer.projects_per_month),
      followup_date: buyer?.followup_date ? String(buyer.followup_date) : "",
      fit: buyer.fit,
      conviction: buyer.conviction,
      committed_date: buyer?.committed_date ? String(buyer.committed_date) : "",
    };
    setEditBuyerModal({
      isOpen: true,
      mode: "single",
      buyerId,
      bulkBuyerIds: [],
      buyerUserName: String(buyerRow?.firstUserName || "").trim(),
      buyerUserMobile: String(buyerRow?.firstUserMobile || "").trim(),
      loading: true,
      saving: false,
      error: null,
      salesNotes: [],
      form: baseForm,
      touched: {},
    });

    if (!buyerId) {
      setEditBuyerModal((prev) => ({ ...prev, loading: false }));
      return;
    }

    try {
      const payload = await getStoreBuyer(buyerId);
      const detail = payload?.buyer || {};
      const firstUser = firstStoreUserForBuyer(detail?.users);
      setEditBuyerModal((prev) => ({
        ...prev,
        loading: false,
        buyerUserName: String(firstUser?.name || prev.buyerUserName || "").trim(),
        buyerUserMobile: String(firstUser?.mobile || prev.buyerUserMobile || "").trim(),
        salesNotes: ensureArray(detail.sales_notes),
        form: {
          ...baseForm,
          user_name: String(firstUser?.name ?? baseForm.user_name).trim(),
          name: String(detail?.name ?? baseForm.name).trim(),
          city: String(detail?.city ?? baseForm.city).trim(),
          profile_type: String(detail?.profile_type ?? baseForm.profile_type).trim(),
          status: String(detail?.status ?? baseForm.status).trim(),
          sub_status: String(detail?.sub_status ?? baseForm.sub_status).trim(),
          engagement_status: String(
            detail?.engagement_status ?? baseForm.engagement_status
          ).trim(),
          sales_owner_id:
            detail?.sales_owner_id == null
              ? ""
              : String(detail.sales_owner_id),
          projects_per_month:
            detail?.projects_per_month == null
              ? ""
              : String(detail.projects_per_month),
          followup_date: detail?.followup_date
            ? String(detail.followup_date)
            : "",
          fit: detail.fit,
          conviction: detail.conviction,
          committed_date: detail?.committed_date
            ? String(detail.committed_date)
            : "",
          new_remark: "",
        },
      }));
    } catch (e) {
      setEditBuyerModal((prev) => ({
        ...prev,
        loading: false,
        error: toErrorText(e, "Unable to load buyer for edit."),
      }));
    }
  };

  const openBulkEditBuyerModal = (buyerIds) => {
    const ids = ensureArray(buyerIds)
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
    if (ids.length === 0) return;

    setEditBuyerModal({
      isOpen: true,
      mode: "bulk",
      buyerId: null,
      bulkBuyerIds: ids,
      buyerUserName: "",
      buyerUserMobile: "",
      loading: false,
      saving: false,
      error: null,
      salesNotes: [],
      form: {
        user_name: "",
        name: "",
        city: "",
        profile_type: "",
        status: "prospect",
        sub_status: "",
        engagement_status: "",
        sales_owner_id: "",
        new_remark: "",
        projects_per_month: "",
        followup_date: "",
        fit: "",
        conviction: "",
        committed_date: "",
      },
      touched: {},
    });
  };

  const closeEditBuyerModal = () => {
    setEditBuyerModal((prev) => ({
      ...prev,
      isOpen: false,
      error: null,
      saving: false,
    }));
  };

  const handleEditBuyerChange = (key, value) => {
    setEditBuyerModal((prev) => ({
      ...prev,
      form: { ...prev.form, [key]: value },
      touched: { ...prev.touched, [key]: true },
      error: null,
    }));
  };

  const saveBuyerEdits = async () => {
    const isBulk = editBuyerModal.mode === "bulk";
    const buyerId = editBuyerModal.buyerId;
    if (!isBulk && !buyerId) return;

    const errors = validateBuyerEditForm(editBuyerModal.form, editBuyerModal.mode);
    if (Object.keys(errors).length > 0) {
      setEditBuyerModal((prev) => ({
        ...prev,
        touched: {
          ...prev.touched,
          ...(isBulk
            ? {
                fit: true,
                conviction: true,
                committed_date: true,
                engagement_status: true,
                sub_status: true,
                sales_owner_id: true,
                bulk_fields: true,
              }
            : {
                user_name: true,
                name: true,
                city: true,
                profile_type: true,
                status: true,
                sub_status: true,
                engagement_status: true,
                projects_per_month: true,
                committed_date: true,
              }),
        },
        error: "Please fix validation errors.",
      }));
      return;
    }

    setEditBuyerModal((prev) => ({ ...prev, saving: true, error: null }));

    try {
      const f = editBuyerModal.form;

      if (isBulk) {
        const fit = String(f.fit || "").trim();
        const eng = String(f.engagement_status || "").trim();
        const sub = String(f.sub_status || "").trim();
        const salesOwner = String(f.sales_owner_id || "").trim();
        const conviction = String(f.conviction || "").trim();
        const committedDate = String(f.committed_date || "").trim();
        const bulkPayload = {};
        if (fit) bulkPayload.fit = fit;
        if (eng) bulkPayload.engagement_status = eng;
        if (sub) bulkPayload.sub_status = sub;
        if (salesOwner) bulkPayload.sales_owner_id = Number(salesOwner);
        if (conviction) bulkPayload.conviction = conviction;
        if (committedDate) {
          bulkPayload.committed_date =
            committedDate === COMMITTED_DATE_FILTER_NONE ? null : committedDate;
        }

        const ids = ensureArray(editBuyerModal.bulkBuyerIds);
        let nextBuyers = buyers;
        let nextLeads = leads;
        let nextContacts = contacts;
        const failedIds = [];

        for (const id of ids) {
          try {
            const existing =
              nextBuyers.find((row) => Number(row?.id) === Number(id)) ||
              nextLeads.find((row) => Number(row?.id) === Number(id)) ||
              nextContacts.find((row) => Number(row?.id) === Number(id));
            const payload = { ...bulkPayload };
            if (
              payload.sub_status &&
              String(existing?.status || "").trim() !== "prospect"
            ) {
              delete payload.sub_status;
            }

            const response = await updateStoreBuyer(id, payload);
            if (response?.success === false) {
              failedIds.push(id);
              continue;
            }
            const editedBuyer = response?.buyer;
            if (!editedBuyer) {
              failedIds.push(id);
              continue;
            }

            const patched = patchBuyerDashboardLists(
              nextBuyers,
              nextLeads,
              nextContacts,
              id,
              editedBuyer
            );
            nextBuyers = patched.buyers;
            nextLeads = patched.leads;
            nextContacts = patched.contacts;
          } catch {
            failedIds.push(id);
          }
        }

        setBuyers(nextBuyers);
        setLeads(nextLeads);
        setContacts(nextContacts);

        if (failedIds.length > 0) {
          setEditBuyerModal((prev) => ({
            ...prev,
            saving: false,
            error: `Updated ${ids.length - failedIds.length} of ${ids.length} buyers. Failed: ${failedIds.join(", ")}`,
          }));
          if (failedIds.length < ids.length) {
            setSelectedProspectBuyerIds([]);
          }
          return;
        }

        setSelectedProspectBuyerIds([]);
        closeEditBuyerModal();
        return;
      }

      const updatePayload = {
        name: f.name.trim().length ? f.name.trim() : null,
        city: f.city.trim().length ? f.city.trim() : null,
        profile_type: f.profile_type.trim().length
          ? f.profile_type.trim()
          : null,
        fit: f.fit,
        conviction: f.conviction,
        status: f.status,
        sub_status: f.sub_status.trim().length ? f.sub_status : null,
        engagement_status: f.engagement_status.trim(),
        projects_per_month:
          f.projects_per_month === "" ? null : Number(f.projects_per_month),
        followup_date:
          String(f.followup_date || "").trim().length > 0
            ? String(f.followup_date).trim()
            : null,
        committed_date:
          String(f.committed_date || "").trim().length > 0
            ? String(f.committed_date).trim()
            : null,
      };
      if (editBuyerModal.touched?.user_name) {
        const userNameRaw = String(f.user_name || "").trim();
        updatePayload.user_name = userNameRaw.length > 0 ? userNameRaw : null;
      }
      if (editBuyerModal.touched?.sales_owner_id) {
        const salesOwnerRaw = String(f.sales_owner_id || "").trim();
        updatePayload.sales_owner_id =
          salesOwnerRaw.length > 0 ? Number(salesOwnerRaw) : null;
      }
      const newRemark = String(f.new_remark || "").trim();
      if (newRemark.length > 0) {
        updatePayload.new_remark = newRemark;
      }
      const response = await updateStoreBuyer(buyerId, updatePayload);
      const editedBuyer = response?.buyer;
      if (editedBuyer) {
        const patched = patchBuyerDashboardLists(
          buyers,
          leads,
          contacts,
          buyerId,
          editedBuyer
        );
        setBuyers(patched.buyers);
        setLeads(patched.leads);
        setContacts(patched.contacts);
        if (
          selectedBuyer &&
          Number(selectedBuyer.id) === Number(buyerId) &&
          patched.mergedBuyer
        ) {
          setSelectedBuyer(patched.mergedBuyer);
        }
      }
      setHighlightBuyerId(Number(buyerId));
      closeEditBuyerModal();
    } catch (e) {
      setEditBuyerModal((prev) => ({
        ...prev,
        saving: false,
        error: toErrorText(e, "Unable to update buyer."),
      }));
    } finally {
      setEditBuyerModal((prev) => ({ ...prev, saving: false }));
    }
  };

  const openBuyersTabWithBuyerHighlight = (buyerIdRaw) => {
    const buyerId = Number(buyerIdRaw);
    if (!Number.isFinite(buyerId) || buyerId <= 0) return;
    setSearchText("");
    const row = rows.find((r) => Number(r.id) === buyerId);
    const status = String(row?.status || "").trim();
    if (status === "contact") selectBuyerTab("contacts");
    else if (status === "lead") selectBuyerTab("leads");
    else if (status === "prospect") selectBuyerTab("prospects");
    else selectBuyerTab("customers");
    setHighlightBuyerId(buyerId);
  };

  const handleCloseBuyer = () => {
    setSelectedBuyer(null);
    setBuyerDetailLoading(false);
    setIsMobileDetailsOpen(false);
  };

  const closeActivitiesPane = () => {
    setActivitiesPane((prev) => ({ ...prev, isOpen: false }));
  };

  const openActivitiesPaneForBuyer = async (buyerRow, opts = {}) => {
    const buyerId = buyerRow?.id;
    if (!buyerId) return;

    const initialTab =
      opts.initialTab === "sessions" ? "sessions" : "activities";
    const sessionPref =
      opts.sessionId != null && opts.sessionId !== ""
        ? String(opts.sessionId)
        : "all";

    setActivitiesPane({
      isOpen: true,
      buyerId,
      buyerName: String(buyerRow?.name || "").trim(),
      buyerMobile: "",
      loading: true,
      error: null,
      activitiesRows: [],
      sessionsRows: [],
      paneTab: initialTab,
      activitySessionFilter: sessionPref,
      sessionsSortConfig: SESSIONS_SORT_DEFAULT,
    });

    try {
      const res = await getStoreBuyerActivities(buyerId);
      const payload = res?.data ?? res;
      const buyer = payload?.buyer || {};
      const acts = ensureArray(payload?.activities);
      const sess = ensureArray(payload?.sessions);
      setActivitiesPane((prev) => ({
        ...prev,
        buyerMobile: String(buyer?.primary_user_mobile || "").trim(),
        loading: false,
        error: null,
        activitiesRows: acts,
        sessionsRows: sess,
      }));
    } catch (e) {
      setActivitiesPane((prev) => ({
        ...prev,
        loading: false,
        error: toErrorText(e, "Unable to load activities."),
        activitiesRows: [],
        sessionsRows: [],
        sessionsSortConfig: SESSIONS_SORT_DEFAULT,
      }));
    }
  };

  const handleProjectClick = (project) => {
    if (!project?.id) return;
    window.open(`/projects/${project.id}`, "_blank", "noopener,noreferrer");
  };

  const closeAssignDealerModal = () => {
    if (assignDealerModal.loading) return;
    setAssignDealerModal((prev) => ({ ...prev, isOpen: false }));
  };

  const handleAssignDealer = async () => {
    const buyerId = assignDealerModal.buyerId;
    const dealerId = Number(assignDealerModal.selectedDealerId);
    if (!buyerId || !dealerId) return;

    setActionError(null);
    setAssignDealerModal((prev) => ({ ...prev, loading: true }));
    try {
      await assignStoreBuyerDealer(buyerId, dealerId);
      await Promise.all([loadDashboard(), loadDealers()]);
      setAssignDealerModal({
        isOpen: false,
        buyerId: null,
        buyerName: "",
        selectedDealerId: "",
        loading: false,
      });
    } catch (e) {
      setActionError(toErrorText(e, "Unable to assign dealer."));
      setAssignDealerModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const openDispatchShipmentModal = (shipment, shipmentIndex) => {
    const shipmentId = shipment?.id;
    if (shipmentId == null) return;
    setActionError(null);
    setDispatchShipmentModal({
      isOpen: true,
      shipmentId,
      shipmentLabel: shipment?.name,
      loading: false,
    });
  };

  const openShipmentInvoiceModal = async (shipment) => {
    const shipmentId = shipment?.id;

    if (shipmentId == null) return;

    setActionError(null);
    setInvoiceModal({
      isOpen: true,
      loading: true,
      error: null,
      shipmentLabel: String(shipment?.name || "").trim() || "Shipment",
      invoice: null,
    });

    try {
      const invoice = (await getStoreShipmentInvoice(shipmentId)).res;
      setInvoiceModal((prev) => ({
        ...prev,
        loading: false,
        invoice,
        error: null,
      }));
    } catch (e) {
      setInvoiceModal((prev) => ({
        ...prev,
        loading: false,
        error: toErrorText(e, "Unable to load invoice."),
        invoice: null,
      }));
    }
  };

  const closeShipmentInvoiceModal = () => {
    setInvoiceModal({
      isOpen: false,
      loading: false,
      error: null,
      shipmentLabel: "",
      invoice: null,
    });
  };

  const closeDispatchShipmentModal = () => {
    if (dispatchShipmentModal.loading) return;
    setDispatchShipmentModal((prev) => ({ ...prev, isOpen: false }));
  };

  const handleConfirmDispatchShipment = async () => {
    const shipmentId = dispatchShipmentModal.shipmentId;
    if (shipmentId == null) return;
    setActionError(null);
    setDispatchShipmentModal((prev) => ({ ...prev, loading: true }));
    try {
      await patchStoreShipmentStatus(shipmentId, "dispatched");
      await loadOrders();
      setDispatchShipmentModal({
        isOpen: false,
        shipmentId: null,
        shipmentLabel: "",
        loading: false,
      });
    } catch (e) {
      setActionError(toErrorText(e, "Unable to dispatch shipment."));
      setDispatchShipmentModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const openDeliverShipmentModal = (shipment, shipmentIndex) => {
    const shipmentId = shipment?.id;
    if (shipmentId == null) return;
    setActionError(null);
    setDeliverShipmentModal({
      isOpen: true,
      shipmentId,
      shipmentLabel: `Shipment ${shipmentIndex + 1}`,
      loading: false,
    });
  };

  const closeDeliverShipmentModal = () => {
    if (deliverShipmentModal.loading) return;
    setDeliverShipmentModal((prev) => ({ ...prev, isOpen: false }));
  };

  const handleConfirmDeliverShipment = async () => {
    const shipmentId = deliverShipmentModal.shipmentId;
    if (shipmentId == null) return;
    setActionError(null);
    setDeliverShipmentModal((prev) => ({ ...prev, loading: true }));
    try {
      await patchStoreShipmentStatus(shipmentId, "delivered");
      await loadOrders();
      setDeliverShipmentModal({
        isOpen: false,
        shipmentId: null,
        shipmentLabel: "",
        loading: false,
      });
    } catch (e) {
      setActionError(toErrorText(e, "Unable to mark shipment delivered."));
      setDeliverShipmentModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const openPaymentsModal = async (order) => {
    const projectId = order?.id;
    if (!projectId) return;
    setPaymentsModal({
      isOpen: true,
      projectId,
      projectName: formatStoreOrderLabel(order),
      mode: String(order?.mode || ""),
      status: String(order?.status || ""),
      loading: true,
      saving: false,
      error: null,
      payments: [],
      total: Number(order?.total_payment_received || 0) || 0,
      value: "",
      date: defaultPaymentDateInputValue(),
      time: defaultPaymentTimeOption(),
    });
    try {
      const payload = await getStoreProjectPayments(projectId);
      setPaymentsModal((prev) => ({
        ...prev,
        loading: false,
        payments: ensureArray(payload?.data?.payments),
        total: Number(payload?.data?.total_payment_received || 0) || 0,
      }));
    } catch (e) {
      setPaymentsModal((prev) => ({
        ...prev,
        loading: false,
        error: toErrorText(e, "Unable to load payments."),
      }));
    }
  };

  const closePaymentsModal = () => {
    if (paymentsModal.saving) return;
    setPaymentsModal((prev) => ({ ...prev, isOpen: false }));
  };

  const submitPayment = async () => {
    if (!paymentsModal.projectId || paymentsModal.saving) return;
    setPaymentsModal((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const paymentAt = combinePaymentDateAndTime(
        paymentsModal.date,
        paymentsModal.time
      );
      await createStoreProjectPayment(paymentsModal.projectId, {
        value: Number(paymentsModal.value),
        date: paymentAt,
      });
      const payload = await getStoreProjectPayments(paymentsModal.projectId);
      setPaymentsModal((prev) => ({
        ...prev,
        saving: false,
        payments: ensureArray(payload?.data?.payments),
        total: Number(payload?.data?.total_payment_received || 0) || 0,
        value: "",
        date: defaultPaymentDateInputValue(),
        time: defaultPaymentTimeOption(),
      }));
      await loadOrders();
    } catch (e) {
      setPaymentsModal((prev) => ({
        ...prev,
        saving: false,
        error: toErrorText(e, "Unable to add payment."),
      }));
    }
  };

  const updateOrderStatus = async (projectId, status, rejectionRemarks = null) => {
    setActionError(null);
    try {
      await updateStoreProjectStatus(projectId, status, null, rejectionRemarks);
      await loadOrders();
      return true;
    } catch (e) {
      setActionError(toErrorText(e, "Unable to update order status."));
      return false;
    }
  };

  const rows = useMemo(
    () => mapBuyersToRows(buyers, storeAdminUsers),
    [buyers, storeAdminUsers]
  );
  const customerOrderMonthKeys = useMemo(() => buildCustomerOrderMonthKeys(), []);
  const buyerOrderMonthStats = useMemo(
    () => buildBuyerOrderMonthStats(orders, customerOrderMonthKeys),
    [orders, customerOrderMonthKeys]
  );
  const customerOrderHeaderStats = useMemo(() => {
    if (
      activeTab !== "customers" ||
      ensureArray(selectedCustomerBuyerIds).length === 0
    ) {
      return buyerOrderMonthStats;
    }

    return aggregateBuyerOrderMonthStatsForBuyerIds(
      buyerOrderMonthStats,
      selectedCustomerBuyerIds,
      customerOrderMonthKeys
    );
  }, [
    activeTab,
    selectedCustomerBuyerIds,
    buyerOrderMonthStats,
    customerOrderMonthKeys,
  ]);
  const rowsWithOrderStats = useMemo(
    () =>
      rows.map((row) => {
        const stats = buyerOrderMonthStats.byBuyerId.get(Number(row.id));

        return {
          ...row,
          customerOrderStats: stats || {
            total: { value: 0, count: 0 },
            months: Object.fromEntries(
              customerOrderMonthKeys.map((key) => [
                key,
                { value: 0, count: 0 },
              ])
            ),
          },
        };
      }),
    [rows, buyerOrderMonthStats, customerOrderMonthKeys]
  );
  const leadRows = useMemo(
    () => mapBuyersToRows(leads, storeAdminUsers),
    [leads, storeAdminUsers]
  );
  const contactRows = useMemo(
    () => mapBuyersToRows(contacts, storeAdminUsers),
    [contacts, storeAdminUsers]
  );

  const sentTemplatesByUserId = useMemo(
    () => buildSentTemplatesByUserId(notifications),
    [notifications]
  );

  const buyerCityOptions = useMemo(() => {
    const sourceRows =
      activeTab === "contacts"
        ? contactRows
        : activeTab === "leads"
          ? leadRows
          : rows;

    return buildBuyerCityOptionsFromRows(sourceRows, buyerStateFilter);
  }, [rows, leadRows, contactRows, activeTab, buyerStateFilter]);

  useEffect(() => {
    if (!BUYER_CITY_FILTER_TABS.includes(activeTab)) return;

    const validCities = new Set(buyerCityOptions);
    const current = ensureArray(buyerCityFilter);
    const pruned = current.filter((city) => validCities.has(city));
    if (pruned.length === current.length) return;

    setBuyerCityFiltersByTab((prev) => ({
      ...prev,
      [activeTab]: pruned,
    }));
  }, [activeTab, buyerCityOptions, buyerCityFilter]);

  const buyerStateOptions = useMemo(() => {
    const set = new Set();
    const sourceRows =
      activeTab === "contacts"
        ? contactRows
        : activeTab === "leads"
          ? leadRows
          : rows;
    sourceRows.forEach((r) => {
      const label = String(r.stateLabel || "").trim();
      if (label && label !== "-") set.add(label);
    });

    return orderBuyerStateOptions(Array.from(set));
  }, [rows, leadRows, contactRows, activeTab]);

  const contactSourceOptions = useMemo(() => {
    const set = new Set();
    contactRows.forEach((r) => {
      const source = String(r.leadSource || "").trim();
      if (source && source !== "-") set.add(source);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [contactRows]);

  const buyerPpmOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      if (r.projectsPerMonth == null) return;
      const n = Number(r.projectsPerMonth);
      if (Number.isFinite(n)) set.add(n);
    });
    return Array.from(set).sort((a, b) => a - b);
  }, [rows]);

  const buyerSalesOwnerFilterOptions = useMemo(() => {
    const opts = [{ value: "__none__", label: "— (unassigned)" }];
    const seen = new Set();
    storeAdminUsers.forEach((user) => {
      const id = user?.id;
      if (id == null || seen.has(id)) return;
      seen.add(id);
      const label =
        String(user?.name || "").trim() ||
        String(user?.mobile || "").trim() ||
        `Admin #${id}`;
      opts.push({ value: String(id), label });
    });
    return opts;
  }, [storeAdminUsers]);

  const buyerAcquiredMonthFilterOptions = useMemo(() => {
    const customerRows = rows.filter(
      (r) =>
        String(r.status || "").trim() === "customer" &&
        String(r.customerStatus || "pending").trim() === "active"
    );
    const monthOptions = buildBuyerAcquiredMonthFilterOptions(customerRows);

    return [
      { value: "__none__", label: "— (not acquired)" },
      ...monthOptions,
    ];
  }, [rows]);

  const tabFilterOptions = useMemo(
    () => ({
      searchText,
      buyerStateFilter,
      buyerCityFilter,
      buyerPpmFilter,
      buyerEngagementFilter,
      buyerSalesOwnerFilter,
      buyerProfileTypeFilter,
      eventSentFilter,
      eventNotSentFilter,
      sentTemplatesByUserId,
      buyerVisibilityFilter,
      buyerContactStatusFilter,
      buyerSourceFilter,
      profileFitFilter,
      convictionFilter,
      committedDateFilter,
      buyerAcquiredMonthFilter,
    }),
    [
      searchText,
      buyerStateFilter,
      buyerCityFilter,
      buyerPpmFilter,
      buyerEngagementFilter,
      buyerSalesOwnerFilter,
      buyerProfileTypeFilter,
      eventSentFilter,
      eventNotSentFilter,
      sentTemplatesByUserId,
      buyerVisibilityFilter,
      buyerContactStatusFilter,
      buyerSourceFilter,
      profileFitFilter,
      convictionFilter,
      committedDateFilter,
      buyerAcquiredMonthFilter,
    ]
  );

  const committedDateFilterOptions = useMemo(() => {
    const prospectRows = rows.filter((r) => {
      if (r.status === "prospect") return true;
      if (r.status !== "customer") return false;
      const customerStatus = String(r.customerStatus || "pending").trim();

      return customerStatus === "sample" || customerStatus === "pending";
    });

    return buildCommittedDateFilterOptions(prospectRows);
  }, [rows]);

  const displayedRows = useMemo(() => {
    let sourceRows = [];
    if (activeTab === "customers") {
      sourceRows = rowsWithOrderStats.filter(
        (r) =>
          r.status === "customer" &&
          String(r.customerStatus || "pending").trim() === "active"
      );
    } else if (activeTab === "prospects") {
      sourceRows = rowsWithOrderStats.filter((r) => {
        if (r.status === "prospect") return true;
        if (r.status !== "customer") return false;
        const customerStatus = String(r.customerStatus || "pending").trim();

        return customerStatus === "sample" || customerStatus === "pending";
      });
    } else if (activeTab === "leads") {
      sourceRows = leadRows;
    } else if (activeTab === "contacts") {
      sourceRows = contactRows;
    } else {
      return [];
    }

    const filtered = applyBuyerTabFilters({
      rows: sourceRows,
      tab: activeTab,
      ...tabFilterOptions,
    });
    return sortRows(filtered, sortConfig);
  }, [
    activeTab,
    rowsWithOrderStats,
    leadRows,
    contactRows,
    tabFilterOptions,
    sortConfig,
  ]);

  const buyersTableViewToggle =
    activeTab === "prospects" || activeTab === "customers";
  const buyersTableWide =
    buyersTableViewToggle && buyersTableViewMode === "wide";
  const buyersTableCompact =
    buyersTableViewToggle && buyersTableViewMode === "compact";
  const buyersTableRemarks =
    buyersTableViewToggle && buyersTableViewMode === "remarks";

  let showStatusColumn;
  let showProfileTypeColumn;
  let showPpmColumn;
  const showDealerColumn = false;
  let showLeadSourceColumn;
  let showEngagementColumn;
  let showSalesOwnerColumn;
  let showLastFollowupColumn;
  let showNextFollowupColumn;
  let showLoginAttemptsColumn;
  let showSessionsColumn;
  let showProjectsColumn;
  let showRemarksInTable;
  const showAcquiredColumn =
    activeTab === "customers" && buyersTableViewMode !== "remarks";
  const showSampleDateColumn =
    activeTab === "prospects" && buyerVisibilityFilter === "sample";

  const showContactFilters = activeTab === "contacts";
  showProjectsColumn = !showContactFilters;

  const showCustomerOrderMonthColumns =
    activeTab === "customers" && buyersTableViewMode !== "remarks";
  const showCustomerSalesPipelineColumns = activeTab !== "customers";

  if (buyersTableWide) {
    showStatusColumn = true;
    showProfileTypeColumn = true;
    showPpmColumn = true;
    showLeadSourceColumn = showCustomerSalesPipelineColumns;
    showEngagementColumn = showCustomerSalesPipelineColumns;
    showSalesOwnerColumn = showCustomerSalesPipelineColumns;
    showLastFollowupColumn = showCustomerSalesPipelineColumns;
    showNextFollowupColumn = showCustomerSalesPipelineColumns;
    showLoginAttemptsColumn = false;
    showSessionsColumn = true;
    showRemarksInTable = showCustomerSalesPipelineColumns;
  } else if (buyersTableRemarks) {
    showStatusColumn = false;
    showProfileTypeColumn = false;
    showPpmColumn = true;
    showLeadSourceColumn = false;
    showEngagementColumn = true;
    showSalesOwnerColumn = false;
    showLastFollowupColumn = true;
    showNextFollowupColumn = false;
    showLoginAttemptsColumn = false;
    showSessionsColumn = false;
    showProjectsColumn = false;
    showRemarksInTable = true;
  } else if (buyersTableCompact) {
    showStatusColumn = false;
    showProfileTypeColumn = true;
    showPpmColumn = activeTab === "prospects";
    showLeadSourceColumn = false;
    showEngagementColumn = false;
    showSalesOwnerColumn = false;
    showLastFollowupColumn = showCustomerSalesPipelineColumns;
    showNextFollowupColumn = showCustomerSalesPipelineColumns;
    showLoginAttemptsColumn = false;
    showSessionsColumn = false;
    showRemarksInTable = false;
  } else {
    showStatusColumn = activeTab !== "customers";
    showProfileTypeColumn = activeTab !== "customers";
    showPpmColumn = activeTab === "prospects";
    showLeadSourceColumn = showCustomerSalesPipelineColumns;
    showEngagementColumn = activeTab === "prospects";
    showSalesOwnerColumn = activeTab === "prospects";
    showLastFollowupColumn = activeTab === "prospects";
    showNextFollowupColumn = activeTab === "prospects";
    showLoginAttemptsColumn = activeTab === "leads";
    showSessionsColumn = true;
    showRemarksInTable = false;
  }

  if (activeTab === "leads") {
    showStatusColumn = false;
    showProjectsColumn = false;
  }

  const dealerRows = useMemo(() => mapDealersToRows(dealers), [dealers]);

  const notificationTemplateOptions = useMemo(
    () =>
      Array.from(
        new Set(
          ensureArray(notifications)
            .map((row) => String(row.templateName || "").trim())
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b)),
    [notifications]
  );

  const notificationStatusOptions = useMemo(
    () =>
      Array.from(
        new Set(
          ensureArray(notifications)
            .map((row) => String(row.deliveryStatus || "").trim())
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b)),
    [notifications]
  );

  const filteredNotifications = useMemo(() => {
    let rows = [...ensureArray(notifications)];
    const query = notificationNameFilter.trim().toLowerCase();
    if (query) {
      rows = rows.filter((row) =>
        String(row.name || "")
          .toLowerCase()
          .includes(query)
      );
    }
    if (notificationTemplateFilter) {
      rows = rows.filter(
        (row) => row.templateName === notificationTemplateFilter
      );
    }
    if (notificationStatusFilter) {
      rows = rows.filter(
        (row) => row.deliveryStatus === notificationStatusFilter
      );
    }
    return sortNotificationRows(rows, notificationSortConfig);
  }, [
    notifications,
    notificationNameFilter,
    notificationTemplateFilter,
    notificationStatusFilter,
    notificationSortConfig,
  ]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return "⇅";
    return sortConfig.direction === "asc" ? "▲" : "▼";
  };

  const handleNotificationsSort = (key) => {
    setNotificationSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedOrders = useMemo(() => {
    const filtered = orders.filter((o) => {
      if (!matchesOrdersStatusFilter(o, ordersStatusFilter)) return false;
      if (!matchesOrdersSampleFilter(o, ordersSampleFilter)) return false;
      return matchesOrdersMonthFilter(o, ordersMonthFilter);
    });
    return sortOrdersRows(filtered, ordersSortConfig);
  }, [
    orders,
    ordersStatusFilter,
    ordersSampleFilter,
    ordersMonthFilter,
    ordersSortConfig,
  ]);

  const handleOrdersSort = (key) => {
    setOrdersSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const renderOrdersSortIcon = (key) => {
    if (ordersSortConfig.key !== key) return "⇅";
    return ordersSortConfig.direction === "asc" ? "▲" : "▼";
  };

  const renderNotificationSortIcon = (key) => {
    if (notificationSortConfig.key !== key) return "⇅";
    return notificationSortConfig.direction === "asc" ? "▲" : "▼";
  };

  const openNotificationsForName = (name) => {
    const normalized = String(name || "").trim();
    setNotificationNameFilter(normalized);
    setActiveTab("notifications");
    if (notifications.length === 0) {
      loadNotifications();
    }
  };

  return (
    <div className="min-vh-100 w-100 mw-100 bg-light">
      <div className="container-fluid py-4 px-3 px-lg-4 w-100 mw-100">
        <Row>
          <Col>
            <div className="store-dashboard-tabbed">
              <div className="d-flex align-items-end flex-wrap gap-2 w-100 mb-0">
                <Nav tabs className="store-dashboard-nav-tabs mb-0 flex-grow-1">
                <NavItem>
                  <NavLink
                    href="#"
                    className="fw-bold"
                    active={activeTab === "customers"}
                    onClick={(e) => {
                      e.preventDefault();
                      selectBuyerTab("customers");
                    }}
                  >
                    Customers
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    href="#"
                    className="fw-bold"
                    active={activeTab === "prospects"}
                    onClick={(e) => {
                      e.preventDefault();
                      selectBuyerTab("prospects");
                    }}
                  >
                    Prospects
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    href="#"
                    className="fw-bold"
                    active={activeTab === "leads"}
                    onClick={(e) => {
                      e.preventDefault();
                      selectBuyerTab("leads");
                    }}
                  >
                    Leads
                  </NavLink>
                </NavItem>
                <NavItem className={SHOW_DEALERS_TAB ? undefined : "d-none"}>
                  <NavLink
                    href="#"
                    className="fw-bold"
                    active={activeTab === "dealers"}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab("dealers");
                    }}
                  >
                    Dealers
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    href="#"
                    className="fw-bold"
                    active={activeTab === "orders"}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab("orders");
                    }}
                  >
                    Orders
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    href="#"
                    className="fw-bold"
                    active={activeTab === "notifications"}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab("notifications");
                    }}
                  >
                    Notifications
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    href="#"
                    className="fw-bold"
                    active={activeTab === "coupons"}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab("coupons");
                    }}
                  >
                    Coupons
                  </NavLink>
                </NavItem>
              </Nav>
                {BUYER_SEARCH_TABS.includes(activeTab)? (
                  <div className="pb-2">
                <Input
                  bsSize="sm"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Buyer name, user name, mobile, id"
                  style={{ width: "350px", maxWidth: "100%" }}
                  className={`${STORE_DASHBOARD_FILTER_INPUT_CLASS} small p-2 ms-auto`}
                />
                    </div>
              ) : null}
              </div>
              <div className="store-dashboard-tab-panel">
                {error ? (
                  <Alert color="danger" className="mb-0" fade={false}>
                    {error}
                  </Alert>
                ) : (
                  <>
                    {importSuccess ? (
                      <Alert
                        color="success"
                        className="mb-3"
                        fade={false}
                        title="rows · created · skipped"
                      >
                        {importSuccess}
                      </Alert>
                    ) : null}
                    {actionError ? (
                      <Alert color="danger" className="mb-3" fade={false}>
                        {actionError}
                      </Alert>
                    ) : null}
                    {BUYER_TABS.includes(activeTab) ? (
                      <StoreSalesDashboardBuyersSection
                        activeTab={activeTab}
                        buyerCityFilter={buyerCityFilter}
                        setBuyerCityFilter={setBuyerCityFilter}
                        buyerCityOptions={buyerCityOptions}
                        buyerStateFilter={buyerStateFilter}
                        setBuyerStateFilter={setBuyerStateFilter}
                        buyerStateOptions={buyerStateOptions}
                        buyerPpmFilter={buyerPpmFilter}
                        setBuyerPpmFilter={setBuyerPpmFilter}
                        buyerPpmOptions={buyerPpmOptions}
                        buyerEngagementFilter={buyerEngagementFilter}
                        setBuyerEngagementFilter={setBuyerEngagementFilter}
                        buyerSalesOwnerFilter={buyerSalesOwnerFilter}
                        setBuyerSalesOwnerFilter={setBuyerSalesOwnerFilter}
                        buyerSalesOwnerFilterOptions={
                          buyerSalesOwnerFilterOptions
                        }
                        buyerContactStatusFilter={buyerContactStatusFilter}
                        setBuyerContactStatusFilter={
                          setBuyerContactStatusFilter
                        }
                        buyerSourceFilter={buyerSourceFilter}
                        setBuyerSourceFilter={setBuyerSourceFilter}
                        profileFitFilter={profileFitFilter}
                        setProfileFitFilter={setProfileFitFilter}
                        convictionFilter={convictionFilter}
                        setConvictionFilter={setConvictionFilter}
                        committedDateFilter={committedDateFilter}
                        setCommittedDateFilter={setCommittedDateFilter}
                        committedDateFilterOptions={committedDateFilterOptions}
                        buyerAcquiredMonthFilter={buyerAcquiredMonthFilter}
                        setBuyerAcquiredMonthFilter={setBuyerAcquiredMonthFilter}
                        buyerAcquiredMonthFilterOptions={
                          buyerAcquiredMonthFilterOptions
                        }
                        selectedProspectBuyerIds={selectedProspectBuyerIds}
                        setSelectedProspectBuyerIds={setSelectedProspectBuyerIds}
                        selectedCustomerBuyerIds={selectedCustomerBuyerIds}
                        setSelectedCustomerBuyerIds={setSelectedCustomerBuyerIds}
                        onOpenBulkEditBuyerModal={openBulkEditBuyerModal}
                        contactSourceOptions={contactSourceOptions}
                        searchText={searchText}
                        setSearchText={setSearchText}
                        buyerVisibilityFilter={buyerVisibilityFilter}
                        setBuyerVisibilityFilter={setBuyerVisibilityFilter}
                        buyersTableViewMode={buyersTableViewMode}
                        setBuyersTableViewMode={setBuyersTableViewMode}
                        displayedRows={displayedRows}
                        highlightBuyerId={highlightBuyerId}
                        showStatusColumn={showStatusColumn}
                        showProfileTypeColumn={showProfileTypeColumn}
                        showPpmColumn={showPpmColumn}
                        showDealerColumn={showDealerColumn}
                        showLeadSourceColumn={showLeadSourceColumn}
                        showEngagementColumn={showEngagementColumn}
                        showSalesOwnerColumn={showSalesOwnerColumn}
                        showLastFollowupColumn={showLastFollowupColumn}
                        showNextFollowupColumn={showNextFollowupColumn}
                        showSessionsColumn={showSessionsColumn}
                        showProjectsColumn={showProjectsColumn}
                        showLoginAttemptsColumn={showLoginAttemptsColumn}
                        showRemarksInTable={showRemarksInTable}
                        showAcquiredColumn={showAcquiredColumn}
                        showSampleDateColumn={showSampleDateColumn}
                        showCustomerOrderMonthColumns={showCustomerOrderMonthColumns}
                        customerOrderMonthKeys={customerOrderMonthKeys}
                        buyerOrderMonthStats={customerOrderHeaderStats}
                        customerOrdersLoading={ordersLoading}
                        handleSort={handleSort}
                        renderSortIcon={renderSortIcon}
                        handleOpenBuyer={handleOpenBuyer}
                        openNotificationsForName={openNotificationsForName}
                        openActivitiesPaneForBuyer={openActivitiesPaneForBuyer}
                        openEditBuyerModal={openEditBuyerModal}
                        contactCsvInputRef={contactCsvInputRef}
                        onContactCsvButtonClick={handleContactCsvButtonClick}
                        onContactCsvFileChange={handleContactCsvFileChange}
                        importCsvLoading={importCsvLoading}
                        buyersLoading={loading}
                      />
                    ) : activeTab === "dealers" ? (
                      <StoreSalesDashboardDealersSection
                        dealersLoading={dealersLoading}
                        dealersError={dealersError}
                        dealerRows={dealerRows}
                        openNotificationsForName={openNotificationsForName}
                      />
                    ) : activeTab === "notifications" ? (
                      <StoreSalesDashboardNotificationsSection
                        notificationsLoading={notificationsLoading}
                        notificationsError={notificationsError}
                        notificationNameFilter={notificationNameFilter}
                        setNotificationNameFilter={setNotificationNameFilter}
                        notificationTemplateFilter={notificationTemplateFilter}
                        setNotificationTemplateFilter={
                          setNotificationTemplateFilter
                        }
                        notificationStatusFilter={notificationStatusFilter}
                        setNotificationStatusFilter={
                          setNotificationStatusFilter
                        }
                        notificationTemplateOptions={
                          notificationTemplateOptions
                        }
                        notificationStatusOptions={notificationStatusOptions}
                        filteredNotifications={filteredNotifications}
                        handleNotificationsSort={handleNotificationsSort}
                        renderNotificationSortIcon={renderNotificationSortIcon}
                      />
                    ) : activeTab === "coupons" ? (
                      <StoreSalesDashboardCouponsSection />
                    ) : activeTab === "orders" ? (
                      <StoreSalesDashboardOrdersSection
                        ordersLoading={ordersLoading}
                        ordersError={ordersError}
                        ordersStatusFilter={ordersStatusFilter}
                        setOrdersStatusFilter={setOrdersStatusFilter}
                        ordersSampleFilter={ordersSampleFilter}
                        setOrdersSampleFilter={setOrdersSampleFilter}
                        ordersMonthFilter={ordersMonthFilter}
                        setOrdersMonthFilter={setOrdersMonthFilter}
                        sortedOrders={sortedOrders}
                        handleOrdersSort={handleOrdersSort}
                        renderOrdersSortIcon={renderOrdersSortIcon}
                        openBuyersTabWithBuyerHighlight={
                          openBuyersTabWithBuyerHighlight
                        }
                        openDispatchShipmentModal={openDispatchShipmentModal}
                        openShipmentInvoiceModal={openShipmentInvoiceModal}
                        openDeliverShipmentModal={openDeliverShipmentModal}
                        openPaymentsModal={openPaymentsModal}
                        updateOrderStatus={updateOrderStatus}
                      />
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <Modal isOpen={assignDealerModal.isOpen} toggle={closeAssignDealerModal}>
        <ModalHeader toggle={closeAssignDealerModal}>Assign Dealer</ModalHeader>
        <ModalBody>
          <div className="mb-2">
            Assigning{" "}
            <span className="fw-semibold">{assignDealerModal.buyerName}</span>
          </div>
          {dealers.length === 0 ? (
            <Alert color="warning" fade={false} className="mb-0">
              No dealers available for assignment.
            </Alert>
          ) : (
            <>
              <Input
                type="select"
                value={assignDealerModal.selectedDealerId}
                onChange={(e) =>
                  setAssignDealerModal((prev) => ({
                    ...prev,
                    selectedDealerId: e.target.value,
                  }))
                }
                disabled={assignDealerModal.loading}
                className="unmask"
              >
                {dealers.map((dealer) => {
                  const user = ensureArray(dealer?.dealer_users)[0];
                  const dealerName =
                    String(dealer?.name || "").trim() || "Unknown";
                  const dealerMobile = String(user?.mobile || "").trim();
                  return (
                    <option key={dealer.id} value={dealer.id}>
                      {dealerName}
                      {dealerMobile ? ` (${dealerMobile})` : ""}
                    </option>
                  );
                })}
              </Input>
              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button
                  color="secondary"
                  outline
                  onClick={closeAssignDealerModal}
                  disabled={assignDealerModal.loading}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  onClick={handleAssignDealer}
                  disabled={
                    assignDealerModal.loading ||
                    !assignDealerModal.selectedDealerId
                  }
                >
                  {assignDealerModal.loading ? "Assigning..." : "Confirm"}
                </Button>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>

      <Modal
        isOpen={dispatchShipmentModal.isOpen}
        toggle={closeDispatchShipmentModal}
        centered
      >
        <ModalHeader toggle={closeDispatchShipmentModal}>
          Confirm Dispatch
        </ModalHeader>
        <ModalBody>
          Are you sure you want to mark{" "}
          <span className="fw-semibold">
            {dispatchShipmentModal.shipmentLabel || "this shipment"}
          </span>{" "}
          as dispatched?
          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button
              color="secondary"
              outline
              onClick={closeDispatchShipmentModal}
              disabled={dispatchShipmentModal.loading}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleConfirmDispatchShipment}
              disabled={dispatchShipmentModal.loading}
            >
              {dispatchShipmentModal.loading ? "Updating..." : "Confirm"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <StoreShipmentInvoiceModal
        isOpen={invoiceModal.isOpen}
        toggle={closeShipmentInvoiceModal}
        shipmentLabel={invoiceModal.shipmentLabel}
        loading={invoiceModal.loading}
        error={invoiceModal.error}
        invoice={invoiceModal.invoice}
      />

      <Modal
        isOpen={deliverShipmentModal.isOpen}
        toggle={closeDeliverShipmentModal}
        centered
      >
        <ModalHeader toggle={closeDeliverShipmentModal}>
          Confirm delivery
        </ModalHeader>
        <ModalBody>
          Are you sure you want to mark{" "}
          <span className="fw-semibold">
            {deliverShipmentModal.shipmentLabel || "this shipment"}
          </span>{" "}
          as delivered?
          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button
              color="secondary"
              outline
              onClick={closeDeliverShipmentModal}
              disabled={deliverShipmentModal.loading}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleConfirmDeliverShipment}
              disabled={deliverShipmentModal.loading}
            >
              {deliverShipmentModal.loading ? "Updating..." : "Confirm"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <Modal isOpen={paymentsModal.isOpen} toggle={closePaymentsModal} centered>
        <ModalHeader toggle={closePaymentsModal}>
          Payments - {paymentsModal.projectName}
        </ModalHeader>
        <ModalBody>
          {paymentsModal.error ? (
            <Alert color="danger" className="py-2">
              {paymentsModal.error}
            </Alert>
          ) : null}
          {paymentsModal.loading ? (
            <div className="text-center py-3">
              <Spinner size="sm" />
            </div>
          ) : (
            <>
              <div className="small mb-2">
                Total received:{" "}
                <span className="fw-semibold">
                  {fmtOrderRupee(paymentsModal.total)}
                </span>
              </div>
              <Table bordered size="sm" className="mb-2">
                <thead>
                  <tr>
                    <th>Date &amp; time</th>
                    <th className="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentsModal.payments.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="text-muted text-center">
                        No payments yet.
                      </td>
                    </tr>
                  ) : (
                    paymentsModal.payments.map((payment) => (
                      <tr key={payment.id}>
                        <td>{formatPaymentDateTime(payment.date)}</td>
                        <td className="text-end">
                          {fmtOrderRupee(payment.value)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
              {canAddPaymentsInSalesModal(
                paymentsModal.mode,
                paymentsModal.status
              ) ? (
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <Input
                    type="number"
                    min="1"
                    placeholder="Amount"
                    className="flex-grow-1"
                    style={{ minWidth: "6rem" }}
                    value={paymentsModal.value}
                    onChange={(e) =>
                      setPaymentsModal((prev) => ({
                        ...prev,
                        value: e.target.value,
                      }))
                    }
                    disabled={paymentsModal.saving}
                  />
                  <Input
                    type="date"
                    value={paymentsModal.date}
                    onChange={(e) =>
                      setPaymentsModal((prev) => ({
                        ...prev,
                        date: e.target.value,
                      }))
                    }
                    disabled={paymentsModal.saving}
                  />
                  <Input
                    type="select"
                    value={paymentsModal.time}
                    onChange={(e) =>
                      setPaymentsModal((prev) => ({
                        ...prev,
                        time: e.target.value,
                      }))
                    }
                    disabled={paymentsModal.saving}
                    style={{ width: "6.5rem" }}
                    aria-label="Payment time (24-hour)"
                  >
                    {PAYMENT_TIME_OPTIONS.map((slot) => (
                      <option key={slot} value={slot}>
                        {slot}
                      </option>
                    ))}
                  </Input>
                  <Button
                    color="primary"
                    disabled={
                      paymentsModal.saving ||
                      !paymentsModal.value ||
                      !paymentsModal.date ||
                      !paymentsModal.time
                    }
                    onClick={submitPayment}
                  >
                    Add
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={closePaymentsModal}
            disabled={paymentsModal.saving}
          >
            Done
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={editBuyerModal.isOpen}
        toggle={closeEditBuyerModal}
        centered
        size="xl"
      >
        <ModalHeader toggle={closeEditBuyerModal}>
          {editBuyerModal.mode === "bulk"
            ? `Bulk update (${ensureArray(editBuyerModal.bulkBuyerIds).length} buyers)`
            : "Edit buyer"}
        </ModalHeader>
        <ModalBody>
          {(() => {
            const isBulk = editBuyerModal.mode === "bulk";
            const errors = validateBuyerEditForm(editBuyerModal.form, editBuyerModal.mode);
            const showErr = (k) =>
              Boolean(editBuyerModal.touched?.[k] && errors[k]);
            const fieldsDisabled =
              editBuyerModal.saving || editBuyerModal.loading;
            const editControlProps = {
              className: "form-control form-control-sm",
              disabled: fieldsDisabled,
            };
            const editSelectProps = {
              className: "form-select form-select-sm",
              disabled: fieldsDisabled,
            };
            return (
              <>
                {editBuyerModal.error ? (
                  <Alert color="danger" className="py-2">
                    {editBuyerModal.error}
                  </Alert>
                ) : null}

                {editBuyerModal.loading ? (
                  <div className="py-4 text-center text-muted">
                    <Spinner className="me-2" />
                    Loading buyer…
                  </div>
                ) : isBulk ? (
                  <>
                    {showErr("bulk_fields") ? (
                      <Alert color="danger" className="py-2">
                        {errors.bulk_fields}
                      </Alert>
                    ) : null}
                    <div className="mb-3">
                      <div className="small text-muted mb-1">Fit</div>
                      <Input
                        type="select"
                        {...editSelectProps}
                        value={editBuyerModal.form.fit}
                        onChange={(e) =>
                          handleEditBuyerChange("fit", e.target.value)
                        }
                        invalid={showErr("fit")}
                      >
                        <option value=""></option>
                        <option value="unknown">Unknown</option>
                        <option value="fit">Fit</option>
                        <option value="not_fit">Not Fit</option>
                      </Input>
                      {showErr("fit") ? (
                        <div className="text-danger small mt-1">
                          {errors.fit}
                        </div>
                      ) : null}
                    </div>
                    <div className="mb-3">
                      <div className="small text-muted mb-1">Conviction</div>
                      <Input
                        type="select"
                        {...editSelectProps}
                        value={editBuyerModal.form.conviction}
                        onChange={(e) =>
                          handleEditBuyerChange("conviction", e.target.value)
                        }
                        invalid={showErr("conviction")}
                      >
                        <option value=""></option>
                        <option value="no">No</option>
                        <option value="may_be">Maybe</option>
                        <option value="yes">Yes</option>
                      </Input>
                      {showErr("conviction") ? (
                        <div className="text-danger small mt-1">
                          {errors.conviction}
                        </div>
                      ) : null}
                    </div>
                    <div className="mb-3">
                      <div className="small text-muted mb-1">
                        Committed date
                      </div>
                      <Input
                        type="select"
                        {...editSelectProps}
                        value={editBuyerModal.form.committed_date}
                        onChange={(e) =>
                          handleEditBuyerChange(
                            "committed_date",
                            e.target.value
                          )
                        }
                        invalid={showErr("committed_date")}
                      >
                        {committedDatePickerOptions(
                          new Date(),
                          editBuyerModal.form.committed_date
                        ).map((opt) => (
                          <option
                            key={opt.value || "__blank"}
                            value={opt.value}
                          >
                            {opt.label}
                          </option>
                        ))}
                      </Input>
                      {showErr("committed_date") ? (
                        <div className="text-danger small mt-1">
                          {errors.committed_date}
                        </div>
                      ) : null}
                    </div>
                    <div className="mb-3">
                      <div className="small text-muted mb-1">
                        Engagement Status
                      </div>
                      <Input
                        type="select"
                        {...editSelectProps}
                        value={editBuyerModal.form.engagement_status}
                        onChange={(e) =>
                          handleEditBuyerChange(
                            "engagement_status",
                            e.target.value
                          )
                        }
                        invalid={showErr("engagement_status")}
                      >
                        <option value=""></option>
                        {BUYER_ENGAGEMENT_STATUS_OPTIONS.map((v) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                      </Input>
                      {showErr("engagement_status") ? (
                        <div className="text-danger small mt-1">
                          {errors.engagement_status}
                        </div>
                      ) : null}
                    </div>
                    <div className="mb-3">
                      <div className="small text-muted mb-1">
                        Prospect Status
                      </div>
                      <Input
                        type="select"
                        {...editSelectProps}
                        value={editBuyerModal.form.sub_status}
                        onChange={(e) =>
                          handleEditBuyerChange("sub_status", e.target.value)
                        }
                        invalid={showErr("sub_status")}
                      >
                        <option value=""></option>
                        {BUYER_PROSPECT_SUB_STATUS_OPTIONS.filter(Boolean).map(
                          (v) => (
                            <option key={v} value={v}>
                              {v}
                            </option>
                          )
                        )}
                      </Input>
                      {showErr("sub_status") ? (
                        <div className="text-danger small mt-1">
                          {errors.sub_status}
                        </div>
                      ) : null}
                    </div>
                    <div className="mb-3">
                      <div className="small text-muted mb-1">Sales owner</div>
                      <Input
                        type="select"
                        {...editSelectProps}
                        value={editBuyerModal.form.sales_owner_id}
                        onChange={(e) =>
                          handleEditBuyerChange(
                            "sales_owner_id",
                            e.target.value
                          )
                        }
                      >
                        <option value=""></option>
                        {storeAdminUsers.map((user) => (
                          <option key={user.id} value={String(user.id)}>
                            {String(user?.name || "").trim() ||
                              String(user?.mobile || "").trim() ||
                              `Admin #${user.id}`}
                          </option>
                        ))}
                      </Input>
                    </div>
                  </>
                ) : (
                  <>
                    <Row className="g-3">
                      <Col md={4}>
                        <div className="mb-3">
                          <div className="small text-muted mb-1">Name</div>
                          <Input
                            {...editControlProps}
                            value={editBuyerModal.form.user_name}
                            onChange={(e) =>
                              handleEditBuyerChange("user_name", e.target.value)
                            }
                            placeholder="Contact name"
                          />
                        </div>

                        <div className="mb-3">
                          <div className="small text-muted mb-1">Company</div>
                          <Input
                            {...editControlProps}
                            value={editBuyerModal.form.name}
                            onChange={(e) =>
                              handleEditBuyerChange("name", e.target.value)
                            }
                            placeholder="Company / buyer name"
                          />
                        </div>

                        <BuyerEditModalMobileField
                          mobile={editBuyerModal.buyerUserMobile}
                        />

                        <div className="mb-3">
                          <div className="small text-muted mb-1">City</div>
                          <Input
                            {...editControlProps}
                            value={editBuyerModal.form.city}
                            onChange={(e) =>
                              handleEditBuyerChange("city", e.target.value)
                            }
                            placeholder="City"
                          />
                        </div>

                        <div className="mb-3">
                          <div className="small text-muted mb-1">
                            Sales owner
                          </div>
                          <Input
                            type="select"
                            {...editSelectProps}
                            value={editBuyerModal.form.sales_owner_id}
                            onChange={(e) =>
                              handleEditBuyerChange(
                                "sales_owner_id",
                                e.target.value
                              )
                            }
                          >
                            <option value="">—</option>
                            {storeAdminUsers.map((user) => (
                              <option key={user.id} value={String(user.id)}>
                                {String(user?.name || "").trim() ||
                                  String(user?.mobile || "").trim() ||
                                  `Admin #${user.id}`}
                              </option>
                            ))}
                          </Input>
                        </div>
                      </Col>

                      <Col md={4}>
                        <div className="mb-3">
                          <div className="small text-muted mb-1">PPM</div>
                          <Input
                            type="number"
                            {...editControlProps}
                            min="1"
                            step="1"
                            value={editBuyerModal.form.projects_per_month}
                            onChange={(e) =>
                              handleEditBuyerChange(
                                "projects_per_month",
                                e.target.value
                              )
                            }
                            placeholder="(optional)"
                            invalid={showErr("projects_per_month")}
                          />
                          {showErr("projects_per_month") ? (
                            <div className="text-danger small mt-1">
                              {errors.projects_per_month}
                            </div>
                          ) : null}
                        </div>

                        <div className="mb-3">
                          <div className="small text-muted mb-1">Fit</div>
                          <Input
                            type="select"
                            {...editSelectProps}
                            value={editBuyerModal.form.fit}
                            onChange={(e) =>
                              handleEditBuyerChange("fit", e.target.value)
                            }
                          >
                            {STORE_BUYER_FIT_VALUES.map((value) => (
                              <option key={value} value={value}>
                                {value === "not_fit"
                                  ? "Not Fit"
                                  : value.charAt(0).toUpperCase() +
                                    value.slice(1)}
                              </option>
                            ))}
                          </Input>
                        </div>

                        <div className="mb-3">
                          <div className="small text-muted mb-1">
                            Conviction
                          </div>
                          <Input
                            type="select"
                            {...editSelectProps}
                            value={editBuyerModal.form.conviction}
                            onChange={(e) =>
                              handleEditBuyerChange(
                                "conviction",
                                e.target.value
                              )
                            }
                          >
                            <option value="">—</option>
                            <option value="no">No</option>
                            <option value="may_be">Maybe</option>
                            <option value="yes">Yes</option>
                          </Input>
                        </div>

                        <div className="mb-3">
                          <div className="small text-muted mb-1">
                            Committed date
                          </div>
                          <Input
                            type="select"
                            {...editSelectProps}
                            value={editBuyerModal.form.committed_date}
                            onChange={(e) =>
                              handleEditBuyerChange(
                                "committed_date",
                                e.target.value
                              )
                            }
                            invalid={showErr("committed_date")}
                          >
                            {committedDatePickerOptions(
                              new Date(),
                              editBuyerModal.form.committed_date
                            ).map((opt) => (
                              <option
                                key={opt.value || "__blank"}
                                value={opt.value}
                              >
                                {opt.label}
                              </option>
                            ))}
                          </Input>
                          {showErr("committed_date") ? (
                            <div className="text-danger small mt-1">
                              {errors.committed_date}
                            </div>
                          ) : null}
                        </div>
                      </Col>

                      <Col md={4}>
                        <div className="mb-3">
                          <div className="small text-muted mb-1">Status</div>
                          <Input
                            type="select"
                            {...editSelectProps}
                            value={editBuyerModal.form.status}
                            onChange={(e) =>
                              handleEditBuyerChange("status", e.target.value)
                            }
                            invalid={showErr("status")}
                          >
                            {BUYER_STATUS_OPTIONS.map((v) => (
                              <option key={v} value={v}>
                                {v}
                              </option>
                            ))}
                          </Input>
                          {showErr("status") ? (
                            <div className="text-danger small mt-1">
                              {errors.status}
                            </div>
                          ) : null}
                        </div>

                        <div className="mb-3">
                          <div className="small text-muted mb-1">
                            Sub Status
                          </div>
                          <Input
                            type="select"
                            {...editSelectProps}
                            value={editBuyerModal.form.sub_status}
                            onChange={(e) =>
                              handleEditBuyerChange(
                                "sub_status",
                                e.target.value
                              )
                            }
                            invalid={showErr("sub_status")}
                          >
                            {BUYER_SUB_STATUS_OPTIONS.map((v) => (
                              <option key={v || "__blank"} value={v}>
                                {v ? v : "—"}
                              </option>
                            ))}
                          </Input>
                          {showErr("sub_status") ? (
                            <div className="text-danger small mt-1">
                              {errors.sub_status}
                            </div>
                          ) : null}
                        </div>

                        <div className="mb-3">
                          <div className="small text-muted mb-1">
                            Engagement Status
                          </div>
                          <Input
                            type="select"
                            {...editSelectProps}
                            value={editBuyerModal.form.engagement_status}
                            onChange={(e) =>
                              handleEditBuyerChange(
                                "engagement_status",
                                e.target.value
                              )
                            }
                            invalid={showErr("engagement_status")}
                          >
                            {BUYER_ENGAGEMENT_STATUS_OPTIONS.map((v) => (
                              <option key={v} value={v}>
                                {v}
                              </option>
                            ))}
                          </Input>
                          {showErr("engagement_status") ? (
                            <div className="text-danger small mt-1">
                              {errors.engagement_status}
                            </div>
                          ) : null}
                        </div>

                        <div className="mb-3">
                          <div className="small text-muted mb-1">
                            Next Follow-up
                          </div>
                          <Input
                            type="date"
                            {...editControlProps}
                            value={editBuyerModal.form.followup_date}
                            onChange={(e) =>
                              handleEditBuyerChange(
                                "followup_date",
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </Col>
                    </Row>

                    {ensureArray(editBuyerModal.salesNotes).length > 0 ? (
                      <div className="mb-3">
                        <div className="small text-muted mb-2">Notes</div>
                        <div className="border rounded p-2 bg-light">
                          {editBuyerModal.salesNotes.map((note) => {
                            const author = String(
                              note?.author_name || ""
                            ).trim();
                            const when = note?.created_at
                              ? fmtHoverDateTime(note.created_at)
                              : "";
                            return (
                              <div
                                key={note?.id ?? `${when}-${note?.body}`}
                                className="small mb-2 pb-2 border-bottom"
                              >
                                <div className="text-muted mb-1">
                                  {when}
                                  {author ? ` · ${author}` : ""}
                                </div>
                                <div className="text-break">{note?.body}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}

                    <div className="mb-3">
                      <Input
                        type="textarea"
                        {...editControlProps}
                        value={editBuyerModal.form.new_remark}
                        onChange={(e) =>
                          handleEditBuyerChange("new_remark", e.target.value)
                        }
                        placeholder="Add notes"
                        rows={3}
                      />
                    </div>
                  </>
                )}

                <div className="d-flex justify-content-end gap-2">
                  <Button
                    color="secondary"
                    outline
                    onClick={closeEditBuyerModal}
                    disabled={editBuyerModal.saving || editBuyerModal.loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    onClick={saveBuyerEdits}
                    disabled={editBuyerModal.saving || editBuyerModal.loading}
                  >
                    {editBuyerModal.saving ? (
                      <>
                        <Spinner size="sm" className="me-2" /> Saving…
                      </>
                    ) : (
                      "Save"
                    )}
                  </Button>
                </div>
              </>
            );
          })()}
        </ModalBody>
      </Modal>

      {BUYER_TABS.includes(activeTab) && !isMobileView ? (
        <Offcanvas
          isOpen={Boolean(selectedBuyer)}
          toggle={handleCloseBuyer}
          direction="end"
          style={{ width: "52%", minWidth: 420 }}
          className="border-start store-sales-offcanvas"
        >
          <OffcanvasHeader toggle={handleCloseBuyer}>
            Customer Details
          </OffcanvasHeader>
          <OffcanvasBody>
            {buyerDetailLoading ? (
              <div className="py-4 text-center text-muted">
                <Spinner className="me-2" />
                Loading buyer…
              </div>
            ) : (
              <BuyerInfoContent
                buyer={selectedBuyer}
                onProjectClick={handleProjectClick}
                detailLoading={buyerDetailLoading}
              />
            )}
          </OffcanvasBody>
        </Offcanvas>
      ) : BUYER_TABS.includes(activeTab) ? (
        <Modal
          isOpen={isMobileDetailsOpen}
          toggle={handleCloseBuyer}
          fullscreen
        >
          <ModalHeader toggle={handleCloseBuyer}>Customer Details</ModalHeader>
          <ModalBody>
            {buyerDetailLoading ? (
              <div className="py-4 text-center text-muted">
                <Spinner className="me-2" />
                Loading buyer…
              </div>
            ) : (
              <BuyerInfoContent
                buyer={selectedBuyer}
                onProjectClick={handleProjectClick}
                detailLoading={buyerDetailLoading}
              />
            )}
          </ModalBody>
        </Modal>
      ) : null}

      {BUYER_TABS.includes(activeTab) && !isMobileView ? (
        <Offcanvas
          isOpen={activitiesPane.isOpen}
          toggle={closeActivitiesPane}
          direction="end"
          style={{ width: "52%", minWidth: 420 }}
          className="border-start store-sales-offcanvas"
        >
          <OffcanvasHeader toggle={closeActivitiesPane}>
            {`${activitiesPane.buyerName || "-"} | ${
              activitiesPane.buyerMobile || "-"
            } - Activities`}
          </OffcanvasHeader>
          <OffcanvasBody>
            <BuyerActivitiesPaneContent
              pane={activitiesPane}
              setPane={setActivitiesPane}
            />
          </OffcanvasBody>
        </Offcanvas>
      ) : BUYER_TABS.includes(activeTab) ? (
        <Modal
          isOpen={activitiesPane.isOpen}
          toggle={closeActivitiesPane}
          fullscreen
        >
          <ModalHeader toggle={closeActivitiesPane}>
            {`${activitiesPane.buyerName || "-"} | ${
              activitiesPane.buyerMobile || "-"
            } - Activities`}
          </ModalHeader>
          <ModalBody>
            <BuyerActivitiesPaneContent
              pane={activitiesPane}
              setPane={setActivitiesPane}
            />
          </ModalBody>
        </Modal>
      ) : null}

    </div>
  );
};

export default StoreSalesDashboard;
